/**
 * Free-form (Canva-style) personaliser editor.
 *
 * Built on Fabric.js. Wires up the DOM scaffold from
 * snippets/flipping-bangers-free-form.liquid:
 *   - Toolbar buttons (add photo, add text, undo, redo, clear)
 *   - Properties panel (text/image-specific + shared object actions)
 *   - Hidden form input for the JSON canvas state at add-to-cart
 *
 * Phase 1 capabilities:
 *   - Photo: file upload, drag/resize/rotate
 *   - Text: font + colour + size + alignment + bold/italic
 *   - Layer order, duplicate, delete
 *   - Print-safe zone (visible inset rectangle)
 *   - localStorage autosave keyed by product handle
 *   - Auto-restore on next visit
 *
 * Phase 3 (deferred) will pipe the canvas into a 3D cylinder preview.
 */
(function () {
  'use strict';

  // Loud version marker so we can confirm the latest JS is actually being
  // served by the CDN / not stuck in browser cache. Bump the date string
  // whenever you ship a real change you want to verify is live.
  var FF_EDITOR_VERSION = '2026-06-28-v68-reveal-gate-ready-signal';

  // ----- Suppress Fabric.js 5.3.0 console spam ------------------------
  // Fabric internally sets ctx.textBaseline = 'alphabetical' (typo for
  // 'alphabetic') during text layout. Browsers warn loudly and fall back
  // to the correct value, but the warning fires on EVERY render — easily
  // 100+ messages per page. Wrap console.warn so messages containing
  // that exact string get silently dropped. Anything else is unaffected.
  (function () {
    if (window.__ffWarnFiltered) return;
    window.__ffWarnFiltered = true;
    var origWarn = console.warn.bind(console);
    console.warn = function () {
      var first = arguments[0];
      if (typeof first === 'string' && first.indexOf("'alphabetical'") !== -1) return;
      origWarn.apply(null, arguments);
    };
  })();
  console.log('%c[ff-editor] loaded ' + FF_EDITOR_VERSION,
              'background:#c75b12;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold;');
  window.__FF_EDITOR_VERSION = FF_EDITOR_VERSION;

  // ============================================================
  // CTA scroll handler + qty/ATC DOM relocation + safety net are
  // shared with the template-driven full-width personaliser, so
  // they live in /assets/sp-personaliser-helpers.js. That file is
  // loaded by the section for any product with a personaliser
  // (free-form OR full-width-mode template). Don't duplicate
  // them here.
  // ============================================================

  // ============================================================
  // Init — wait for Fabric to load (deferred from CDN), then boot.
  // ============================================================
  function whenFabricReady(cb) {
    if (window.fabric) return cb();
    var tries = 0;
    var iv = setInterval(function () {
      if (window.fabric) {
        clearInterval(iv);
        cb();
      } else if (++tries > 100) {
        clearInterval(iv);
        console.error('[ff-editor] Fabric.js failed to load within 10s.');
      }
    }, 100);
  }

  whenFabricReady(boot);

  function boot() {
    var root = document.querySelector('[data-ff-editor]');
    if (!root) return;

    // ---- Read config ---------------------------------------------------
    var configEl = root.querySelector('[data-ff-config]');
    var CONFIG;
    try {
      CONFIG = JSON.parse(configEl.textContent);
    } catch (e) {
      console.error('[ff-editor] invalid config JSON', e);
      return;
    }

    // available_fonts may arrive as a JSON array string or as an actual
    // array depending on Liquid path — normalise.
    var FONTS = parseListField(CONFIG.available_fonts);
    if (!FONTS.length) {
      console.warn('[ff-editor] available_fonts empty — falling back to 3-font default. ' +
                   'Either the metaobject field is empty, or Liquid emission is wrong.');
      FONTS = ['lilita-one', 'charles-wright', 'source-serif'];
    }
    var PALETTE = parseListField(CONFIG.colour_palette);

    // Map font slugs (used in the metaobject + line-item-property data)
    // to actual CSS font-family names. Every slug listed here is loaded
    // by the snippet (Google Fonts <link> for most, @font-face for
    // Charles Wright). To add more fonts: extend the metaobject's
    // available_fonts list, add the slug→family entry below, and add
    // the family to the snippet's combined Google Fonts URL.
    var FONT_FAMILY_MAP = {
      'lilita-one':       'Lilita One',
      'anton':            'Anton',
      'bebas-neue':       'Bebas Neue',
      'oswald':           'Oswald',
      'black-ops-one':    'Black Ops One',
      'pacifico':         'Pacifico',
      'lobster':          'Lobster',
      'permanent-marker': 'Permanent Marker',
      'caveat':           'Caveat',
      'dancing-script':   'Dancing Script',
      'great-vibes':      'Great Vibes',
      'roboto':           'Roboto',
      'open-sans':        'Open Sans',
      'lato':             'Lato',
      'montserrat':       'Montserrat',
      'poppins':          'Poppins',
      'playfair-display': 'Playfair Display',
      'merriweather':     'Merriweather',
      'source-serif':     'Source Serif 4',
      'charles-wright':   'Charles Wright',
    };

    // ---- DOM refs ------------------------------------------------------
    var canvasEl     = root.querySelector('[data-ff-canvas]');
    var canvasWrap   = root.querySelector('[data-ff-canvas-wrap]');
    var addPhotoBtn  = root.querySelector('[data-ff-add-photo]');
    var photoInput   = root.querySelector('[data-ff-photo-input]');
    var ucUploader   = root.querySelector('[data-ff-uc-uploader]');
    var ucCtx        = root.querySelector('[data-ff-uc-ctx]');
    var addTextBtn   = root.querySelector('[data-ff-add-text]');
    var addStickerBtn= root.querySelector('[data-ff-add-sticker]');
    var addBgBtn       = root.querySelector('[data-ff-add-background]');
    var bgModal        = root.querySelector('[data-ff-bg-modal]');
    var bgSwatchesEl   = root.querySelector('[data-ff-bg-swatches]');
    var bgPatternsEl   = root.querySelector('[data-ff-bg-patterns]');
    var bgCustomHex    = root.querySelector('[data-ff-bg-custom-hex]');
    var bgCustomApply  = root.querySelector('[data-ff-bg-custom-apply]');
    var bgClearBtn     = root.querySelector('[data-ff-bg-clear]');
    var bgCloseBtns    = root.querySelectorAll('[data-ff-bg-close]');
    var undoBtn      = root.querySelector('[data-ff-undo]');
    var redoBtn      = root.querySelector('[data-ff-redo]');
    var previewBtn   = root.querySelector('[data-ff-preview]');
    var clearBtn     = root.querySelector('[data-ff-clear]');
    var scrollTo3dBtn= root.querySelector('[data-ff-scroll-to-3d]');

    var stickerModal      = root.querySelector('[data-ff-sticker-modal]');
    var stickerInput      = root.querySelector('[data-ff-sticker-input]');
    var stickerChips      = root.querySelectorAll('[data-ff-sticker-chips] [data-q]');
    var stickerResults    = root.querySelector('[data-ff-sticker-results]');
    var stickerCloseBtns  = root.querySelectorAll('[data-ff-sticker-close]');

    var previewModal     = root.querySelector('[data-ff-preview-modal]');
    var previewImg       = root.querySelector('[data-ff-preview-img]');
    var previewCloseBtns = root.querySelectorAll('[data-ff-preview-close]');

    var emptyPanel   = root.querySelector('[data-ff-properties-empty]');
    var textPanel    = root.querySelector('[data-ff-properties-text]');
    var imagePanel   = root.querySelector('[data-ff-properties-image]');
    var cropPanel    = root.querySelector('[data-ff-properties-crop]');
    var objectActions= root.querySelector('[data-ff-object-actions]');

    var textFont     = root.querySelector('[data-ff-text-font]');
    var fontPicker        = root.querySelector('[data-ff-font-picker]');
    var fontPickerTrigger = root.querySelector('[data-ff-font-picker-trigger]');
    var fontPickerCurrent = root.querySelector('[data-ff-font-picker-current]');
    var fontPickerMenu    = root.querySelector('[data-ff-font-picker-menu]');
    var textSize     = root.querySelector('[data-ff-text-size]');
    var textSizeVal  = root.querySelector('[data-ff-text-size-val]');
    var textColor    = root.querySelector('[data-ff-text-color]');
    var textBold     = root.querySelector('[data-ff-text-bold]');
    var textItalic   = root.querySelector('[data-ff-text-italic]');
    var textAlignBtns= root.querySelectorAll('[data-ff-align]');
    var textLineHeight    = root.querySelector('[data-ff-text-line-height]');
    var textLineHeightVal = root.querySelector('[data-ff-text-line-height-val]');
    var textCharSpacing   = root.querySelector('[data-ff-text-char-spacing]');
    var textCharSpacingVal= root.querySelector('[data-ff-text-char-spacing-val]');

    var imageOpacity     = root.querySelector('[data-ff-image-opacity]');
    var lowResWarn       = root.querySelector('[data-ff-low-res-warn]');
    var stickerPanel        = root.querySelector('[data-ff-properties-sticker]');
    var stickerOpacity      = root.querySelector('[data-ff-sticker-opacity]');
    var stickerOpacityVal   = root.querySelector('[data-ff-sticker-opacity-val]');
    var imageOpacityVal  = root.querySelector('[data-ff-image-opacity-val]');
    var imageCropBtn     = root.querySelector('[data-ff-image-crop]');
    var imageReplaceBtn  = root.querySelector('[data-ff-image-replace]');
    var cropApplyBtn     = root.querySelector('[data-ff-crop-apply]');
    var cropCancelBtn    = root.querySelector('[data-ff-crop-cancel]');

    var bringForwardBtn = root.querySelector('[data-ff-bring-forward]');
    var sendBackwardBtn = root.querySelector('[data-ff-send-backward]');
    var duplicateBtn    = root.querySelector('[data-ff-duplicate]');
    var deleteBtn       = root.querySelector('[data-ff-delete]');

    var canvasDataInput   = root.querySelector('[data-ff-canvas-data]');
    var designSummaryInput= root.querySelector('[data-ff-design-summary]');

    // ---- Populate font selector ---------------------------------------
    // Each <option> is styled with its own font-family so the dropdown
    // doubles as a visual font preview. Browser support varies — some
    // (e.g. Safari) ignore option font-family, but the canvas always
    // renders correctly regardless.
    FONTS.forEach(function (slug) {
      var opt = document.createElement('option');
      opt.value = slug;
      // Prefer the human-readable family name; fall back to the slug.
      opt.textContent = FONT_FAMILY_MAP[slug] || slugToLabel(slug);
      opt.style.fontFamily = slugToFontFamily(slug);
      textFont.appendChild(opt);
    });

    // ---- Custom font picker ----------------------------------------
    // Native <option> font-family is honoured inconsistently across
    // browsers (Safari ignores it). We build a custom dropdown that
    // shows each choice in its own font, and proxy clicks back to the
    // native <select> so existing change-handler logic keeps working.
    if (fontPicker && fontPickerMenu && fontPickerTrigger) {
      // Build menu items.
      FONTS.forEach(function (slug) {
        var label = FONT_FAMILY_MAP[slug] || slugToLabel(slug);
        var li = document.createElement('li');
        li.className = 'ff-font-picker-item';
        li.setAttribute('role', 'option');
        li.setAttribute('data-slug', slug);
        li.textContent = label;
        li.style.fontFamily = slugToFontFamily(slug);
        fontPickerMenu.appendChild(li);
      });

      // Reflect the currently selected font on the trigger button — the
      // current label rendered in its own font, like the menu items.
      function syncFontPickerToNative() {
        var slug = textFont.value || FONTS[0];
        var label = FONT_FAMILY_MAP[slug] || slugToLabel(slug);
        fontPickerCurrent.textContent = label;
        fontPickerCurrent.style.fontFamily = slugToFontFamily(slug);
        // Mark the matching menu item as selected for visual feedback.
        var items = fontPickerMenu.querySelectorAll('.ff-font-picker-item');
        items.forEach(function (el) {
          el.removeAttribute('data-selected');
          if (el.getAttribute('data-slug') === slug) {
            el.setAttribute('data-selected', 'true');
          }
        });
      }
      syncFontPickerToNative();

      function openFontPicker() {
        fontPickerMenu.hidden = false;
        fontPickerTrigger.setAttribute('aria-expanded', 'true');
        // Scroll the selected item into view so a long list opens at
        // the right spot rather than always at the top.
        var sel = fontPickerMenu.querySelector('[data-selected="true"]');
        if (sel) sel.scrollIntoView({ block: 'nearest' });
      }
      function closeFontPicker() {
        fontPickerMenu.hidden = true;
        fontPickerTrigger.setAttribute('aria-expanded', 'false');
      }

      fontPickerTrigger.addEventListener('click', function (e) {
        e.stopPropagation();
        if (fontPickerMenu.hidden) openFontPicker();
        else                       closeFontPicker();
      });

      fontPickerMenu.addEventListener('click', function (e) {
        var item = e.target.closest('.ff-font-picker-item');
        if (!item) return;
        var slug = item.getAttribute('data-slug');
        // Push the choice into the native select and fire its change
        // event — the existing textFont change-handler does all the
        // real work (font preload, fontFamily set, dimension recalc,
        // history save). This keeps a single source of truth.
        textFont.value = slug;
        textFont.dispatchEvent(new Event('change', { bubbles: true }));
        syncFontPickerToNative();
        closeFontPicker();
      });

      // Close on outside click — picker doesn't trap focus, behaves
      // like a normal dropdown.
      document.addEventListener('click', function (e) {
        if (fontPickerMenu.hidden) return;
        if (!fontPicker.contains(e.target)) closeFontPicker();
      });
      // Close on Escape too.
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !fontPickerMenu.hidden) {
          closeFontPicker();
          fontPickerTrigger.focus();
        }
      });

      // Keep the picker label in sync if some other code path changes
      // the native select (e.g. selecting a different text object on
      // the canvas — we already fire textFont.value updates there).
      textFont.addEventListener('change', syncFontPickerToNative);
    }

    // ---- Force-load every available font into the document ------------
    // Google Fonts <link> uses display=swap, which means the browser
    // doesn't actually fetch the font until a DOM element renders text
    // in it. Canvas rendering doesn't always trigger that — so when a
    // customer picks a font, Fabric draws with the fallback (showing
    // the wrong glyphs) until the font finally loads.
    //
    // Calling document.fonts.load() with a CSS-style font shorthand
    // tells the browser "I'm about to use this — fetch it now". Once
    // resolved, the font is in the document's font set and Fabric will
    // render with it correctly. We do this once on boot for all fonts
    // and again whenever the customer picks a different font, just in
    // case the boot-time prefetch failed for any reason.
    function preloadFontsForCanvas(slugs) {
      if (!document.fonts || typeof document.fonts.load !== 'function') return;
      slugs.forEach(function (slug) {
        var family = FONT_FAMILY_MAP[slug] || slug;
        // Several sizes/weights — covers 99% of customer inputs without
        // a long load list. The browser dedupes and only fetches once.
        ['16px', '32px', '48px', 'bold 32px', 'italic 32px'].forEach(function (size) {
          try { document.fonts.load(size + " '" + family + "'"); }
          catch (e) { /* swallow — bad font name shouldn't break boot */ }
        });
      });
    }
    preloadFontsForCanvas(FONTS);

    // ---- Init Fabric canvas -------------------------------------------
    // The canvas is sized to the wrap's available width; height scales
    // proportionally to maintain the configured canvas aspect.
    var aspect = CONFIG.canvas_width / CONFIG.canvas_height;
    var canvasW = canvasWrap.clientWidth || 800;
    var canvasH = Math.round(canvasW / aspect);

    var fabricCanvas = new fabric.Canvas(canvasEl, {
      width:  canvasW,
      height: canvasH,
      backgroundColor: CONFIG.background_colour || '#ffffff',
      preserveObjectStacking: true,
      selection: true,
    });

    // Keep canvas size synced with wrap width on resize. Two
    // mechanisms:
    //  1. window resize — handles browser window changes.
    //  2. ResizeObserver on the wrap — handles in-page layout shifts
    //     (e.g. CSS grid track changing as fonts/images load, sidebar
    //     toggling, etc). Without this, the canvas keeps the size it
    //     had at first boot even when the wrap grows around it.
    var resizeRAF = null;
    function scheduleResize() {
      if (resizeRAF) cancelAnimationFrame(resizeRAF);
      resizeRAF = requestAnimationFrame(resizeCanvas);
    }
    window.addEventListener('resize', scheduleResize);
    if (typeof ResizeObserver !== 'undefined') {
      var ro = new ResizeObserver(scheduleResize);
      ro.observe(canvasWrap);
    }
    // Also schedule a resize shortly after boot, after the page has
    // had a chance to fully lay out (web fonts, gallery images, etc
    // can trigger layout shifts that the initial sync measurement
    // missed).
    setTimeout(scheduleResize, 100);
    setTimeout(scheduleResize, 500);
    function resizeCanvas() {
      var newW = canvasWrap.clientWidth;
      if (newW <= 0) return;
      var newH = Math.round(newW / aspect);
      var scaleX = newW / fabricCanvas.getWidth();
      var scaleY = newH / fabricCanvas.getHeight();
      fabricCanvas.setDimensions({ width: newW, height: newH });
      // Scale all existing objects to match — keeps relative positions.
      fabricCanvas.getObjects().forEach(function (o) {
        if (o.data && o.data.role === 'safe-zone') return;
        o.set({
          left:    o.left   * scaleX,
          top:     o.top    * scaleY,
          scaleX:  o.scaleX * scaleX,
          scaleY:  o.scaleY * scaleY,
        });
        o.setCoords();
      });
      drawSafeZone();
      drawGuidelines();
      fabricCanvas.requestRenderAll();
    }

    // ---- Vertical guidelines + snap -----------------------------------
    //
    // Two dashed vertical lines at 30% and 70% of canvas width — the
    // approximate centres of the mug's visible front and back faces
    // when the design wraps onto a cylinder (with default wrap_fraction).
    // Objects snap their horizontal centre to the nearest guideline
    // when dragged within SNAP_THRESHOLD_PX of it.
    //
    // Drawn as Fabric.Line objects (non-selectable, non-evented,
    // excluded from export). We bring them to the front whenever a
    // user object is added, so they always overlay user content.
    // Snap guidelines on the flat editor canvas:
    //   0.215 / 0.785 — print-team markers (Danni, 4 May 2026), edges
    //                   of the visible front face when wrapped.
    //   0.5           — centre of the design = dead centre of the mug
    //                   front when wrapped. Standard alignment guide.
    var GUIDELINE_FRACS      = [0.215, 0.5, 0.785];
    // Horizontal guideline(s) at this fraction of canvas HEIGHT.
    // Just the centreline for now — most useful single addition for
    // customers placing a single line of text or a centred logo.
    // Same SNAP_THRESHOLD_PX / SNAP_RELEASE_PX apply to both axes.
    var GUIDELINE_Y_FRACS    = [0.5];
    var SNAP_THRESHOLD_PX    = 18;
    var SNAP_RELEASE_PX      = 35;
    // Snap state: when an object's centre crosses into a guideline's
    // catchment, we lockMovementX on it (so Fabric's mouse-tracking
    // can't drag the X back) and remember the mouse X at engagement.
    // When the mouse moves more than SNAP_RELEASE_PX past that point,
    // unlock and let Fabric resume tracking.
    var snapState = null;            // { obj, snapX, snapIdx, anchorMouseX } when locked
    // Y-axis snap state — independent from X. An object can be locked
    // on neither axis, X only, Y only, or BOTH (lands at canvas dead
    // centre). Each axis releases independently when its pointer drift
    // exceeds SNAP_RELEASE_PX.
    var snapStateY = null;           // { obj, snapY, snapIdx, anchorMouseY } when locked

    // A scratch canvas used purely for measuring text ink bounds via
    // ctx.measureText. Reused across calls so we don't allocate per-frame.
    var _measureCanvas = null;
    function _getMeasureCtx() {
      if (!_measureCanvas) _measureCanvas = document.createElement('canvas');
      return _measureCanvas.getContext('2d');
    }

    // Returns the canvas-pixel offset between an object's bounding-rect
    // centre and the actual ink centre of its rendered glyphs.
    //
    // Why this exists: Fabric's bounding rect is based on advance widths
    // (the layout box), but the visible ink doesn't necessarily fill that
    // box symmetrically. Display fonts in particular often have glyphs
    // whose left/right side bearings differ — so the visual midpoint of
    // "Your text" can sit several pixels left of the layout-box midpoint.
    //
    // Snapping on the layout box would put the box on the line but leave
    // the visible text noticeably off-line. We snap on the ink centre so
    // the customer sees the text on the line.
    //
    // Returns null if measurement isn't available (very old browsers).
    function measureTextInkOffset(obj) {
      try {
        var ctx = _getMeasureCtx();
        var fontSize   = obj.fontSize   || 40;
        var fontFamily = obj.fontFamily || 'sans-serif';
        var fontWeight = obj.fontWeight || 'normal';
        var fontStyle  = obj.fontStyle  || 'normal';
        // IMPORTANT: don't wrap fontFamily in extra quotes — Fabric stores
        // it as already-valid CSS ('Lilita One', sans-serif), so wrapping
        // gives "'Lilita One', sans-serif" which is invalid and falls
        // back to the default font, defeating the whole point of measuring.
        ctx.font = fontStyle + ' ' + fontWeight + ' ' + fontSize + 'px ' + fontFamily;

        var lines = (obj._textLines && obj._textLines.length)
          ? obj._textLines.map(function (arr) { return Array.isArray(arr) ? arr.join('') : arr; })
          : (obj.text || '').split('\n');

        var minInkLeft  =  Infinity;
        var maxInkRight = -Infinity;

        for (var li = 0; li < lines.length; li++) {
          var line = lines[li];
          if (!line) continue;
          var m = ctx.measureText(line);
          if (m.actualBoundingBoxLeft == null || m.actualBoundingBoxRight == null) {
            return null;
          }
          var advance  = m.width;
          // Ink extents relative to the line's drawing origin (left edge of
          // its advance box). actualBoundingBoxLeft is the positive distance
          // from the origin to the leftmost ink, so ink-left in origin-space
          // is -actualBoundingBoxLeft. actualBoundingBoxRight is positive
          // distance from origin to rightmost ink.
          var inkLeft  = -m.actualBoundingBoxLeft;
          var inkRight =  m.actualBoundingBoxRight;

          // Where this line sits within obj.width depends on textAlign.
          var lineShift = 0;
          if (obj.textAlign === 'center') lineShift = (obj.width - advance) / 2;
          if (obj.textAlign === 'right')  lineShift =  obj.width - advance;

          var lineInkLeft  = lineShift + inkLeft;
          var lineInkRight = lineShift + inkRight;
          if (lineInkLeft  < minInkLeft)  minInkLeft  = lineInkLeft;
          if (lineInkRight > maxInkRight) maxInkRight = lineInkRight;
        }

        if (minInkLeft === Infinity) return null;

        // Object-local: where is the ink centre vs the box centre?
        var inkCentreLocal = (minInkLeft + maxInkRight) / 2;
        var boxCentreLocal = (obj.width || 0) / 2;
        var localOffset = inkCentreLocal - boxCentreLocal;
        // Convert object-local px → canvas px via scaleX. (No rotation
        // handling needed — these editor objects don't carry rotation
        // mid-snap; if they did the X-component would need cos(angle).)
        return localOffset * (obj.scaleX || 1);
      } catch (err) {
        // Don't let a measurement failure break drag handling.
        return null;
      }
    }

    // Returns {visibleCentre, textOffset} for an object on the canvas.
    // For text objects, the bounding rect (advance-width based) doesn't
    // necessarily match the visual ink centre — the customer sees the
    // ink, so we snap on that. textOffset = visibleCentre - boxCentre,
    // i.e. how far right (positive) the visible centre is of the box.
    function getCentreInfo(obj) {
      var bounds    = obj.getBoundingRect();
      var boxCentre = bounds.left + bounds.width / 2;
      var textOffset = 0;
      var isText = (obj.type === 'i-text' || obj.type === 'text' || obj.type === 'textbox');

      if (isText && obj.text) {
        // Preferred: actual rendered ink centre via measureText.
        var inkOffset = measureTextInkOffset(obj);
        if (inkOffset != null) {
          textOffset = inkOffset;
        } else if (typeof obj.calcTextWidth === 'function') {
          // Fallback for browsers without actualBoundingBox support:
          // adjust by textAlign-based slack of advance-width vs box.
          var scale = (obj.scaleX || 1);
          var textWidth = obj.calcTextWidth() * scale;
          if (textWidth > 0 && textWidth < bounds.width - 1) {
            var slack = bounds.width - textWidth;
            if (obj.textAlign === 'left')  textOffset = -slack / 2;
            if (obj.textAlign === 'right') textOffset =  slack / 2;
          }
        }
      }

      return {
        boxCentre:     boxCentre,
        visibleCentre: boxCentre + textOffset,
        textOffset:    textOffset,
      };
    }
    // After releasing from a guideline, skip re-engagement on THAT
    // guideline for the rest of the current drag — otherwise the
    // object's centre is still effectively on the snap line at
    // release moment, and engage fires again immediately, locking
    // the customer in. Reset on mouse:up so the next drag can engage.
    var releasedFromIdx  = null;  // X axis
    var releasedFromIdxY = null;  // Y axis (independent)

    // Cache of the live fabric.Line objects we use as guidelines. The
    // SNAP code uses these objects' actual rendered positions as the
    // snap target — which guarantees text alignment to where the line
    // visually IS, regardless of any sub-pixel offsets Fabric introduces.
    var guidelineObjs  = [];   // vertical lines (X-axis snap)
    var guidelineObjsY = [];   // horizontal lines (Y-axis snap)

    function drawGuidelines() {
      // Remove any existing guideline objects (called on init + resize).
      // Both axes share the 'guideline' role but live in separate arrays;
      // the cleanup pass catches both kinds in one sweep.
      fabricCanvas.getObjects().forEach(function (o) {
        if (o.data && o.data.role === 'guideline') {
          fabricCanvas.remove(o);
        }
      });
      guidelineObjs  = [];
      guidelineObjsY = [];
      var w = fabricCanvas.getWidth();
      var h = fabricCanvas.getHeight();
      // Shared visual treatment so X and Y guidelines read as one
      // family — same colour/alpha/dash pattern, only the orientation
      // differs.
      var lineStyle = {
        stroke: 'rgba(199, 91, 18, 0.85)',
        strokeDashArray: [8, 6],
        strokeWidth: 2,
        selectable: false,
        evented: false,
        excludeFromExport: true,
        hoverCursor: 'default',
      };
      // Vertical lines for the X-axis snap (print-team markers at
      // 0.215 / 0.785 + dead-centre at 0.5).
      GUIDELINE_FRACS.forEach(function (frac, idx) {
        var x = w * frac;
        var line = new fabric.Line([x, 0, x, h], lineStyle);
        line.data = { role: 'guideline', axis: 'x', position: frac, fracIdx: idx };
        fabricCanvas.add(line);
        guidelineObjs[idx] = line;
      });
      // Horizontal line(s) for the Y-axis snap (centre of canvas height
      // at 0.5). Same visual style — combined with the vertical centre
      // line at 0.5, they form a crosshair at the dead-centre of the
      // design that customers can land on.
      GUIDELINE_Y_FRACS.forEach(function (frac, idx) {
        var y = h * frac;
        var line = new fabric.Line([0, y, w, y], lineStyle);
        line.data = { role: 'guideline', axis: 'y', position: frac, fracIdx: idx };
        fabricCanvas.add(line);
        guidelineObjsY[idx] = line;
      });
      bringGuidelinesToFront();
    }

    // Returns the X canvas-coordinate that the i-th guideline ACTUALLY
    // renders at — using the object's bounding rect (which reflects
    // Fabric's internal positioning + stroke offsets), not the
    // geometric formula we passed in. Falls back to the formula if
    // the object isn't available for any reason.
    function getGuidelineCentreX(i) {
      var line = guidelineObjs[i];
      if (line) {
        var b = line.getBoundingRect();
        return b.left + b.width / 2;
      }
      return fabricCanvas.getWidth() * GUIDELINE_FRACS[i];
    }
    // Y-axis equivalent — returns the rendered Y centre of the i-th
    // horizontal guideline.
    function getGuidelineCentreY(i) {
      var line = guidelineObjsY[i];
      if (line) {
        var b = line.getBoundingRect();
        return b.top + b.height / 2;
      }
      return fabricCanvas.getHeight() * GUIDELINE_Y_FRACS[i];
    }

    function bringGuidelinesToFront() {
      fabricCanvas.getObjects().forEach(function (o) {
        if (o.data && o.data.role === 'guideline') {
          fabricCanvas.bringToFront(o);
        }
      });
    }

    // Snap with movement-lock approach:
    //
    // The previous "override obj.left in object:moving" approach fought
    // Fabric's continuous mouse-tracking — every frame Fabric recomputed
    // obj.left from the mouse delta, then we overrode, then Fabric
    // recomputed, etc. Visual result: shake.
    //
    // Now: when the object's centre enters the guideline's catchment, we
    // (a) snap it to the line and (b) set obj.lockMovementX = true so
    // Fabric's mouse-tracking can't change the X any more. We track the
    // mouse position at engagement; when the mouse drifts more than
    // SNAP_RELEASE_PX past that point, we unlock and let Fabric resume
    // tracking from the current position (the snap "releases").
    fabricCanvas.on('object:moving', function (e) {
      var obj = e.target;
      if (!obj) return;
      if (obj.data) {
        var role = obj.data.role;
        if (role === 'guideline' || role === 'safe-zone' || role === 'crop-rect') return;
      }

      var pointer = e.pointer || (e.e && fabricCanvas.getPointer(e.e));
      var mouseX  = pointer ? pointer.x : null;
      var mouseY  = pointer ? pointer.y : null;

      // ----- X-axis engagement -----
      // Skip if already locked on X. Note: we no longer return from the
      // whole handler when X is locked, because Y might still need to
      // engage on the same frame (customer drags toward dead centre
      // and crosses the X line first, then the Y line moments later).
      if (!snapState || snapState.obj !== obj) {
        var info    = getCentreInfo(obj);
        var centerX = info.visibleCentre;  // snap-target centre (text-aware)
        for (var i = 0; i < GUIDELINE_FRACS.length; i++) {
          // Skip the line we just released from this drag (otherwise
          // engage fires again on the very next frame because the
          // object's centre is still at the snap position).
          if (i === releasedFromIdx) continue;
          // Use the line's ACTUAL rendered X (not the geometric formula)
          // so visible-ink-centre lines up with the visible line stroke.
          var snapX = getGuidelineCentreX(i);
          var diff  = centerX - snapX;
          if (Math.abs(diff) < SNAP_THRESHOLD_PX) {
            // Use Fabric's setPositionByOrigin — explicitly places the
            // object's centre at (snapX, currentY). Unlike `set({ left })`,
            // this handles text/origin/scale quirks consistently.
            // For text where the visible centre differs from the box centre,
            // we shift the target by the textOffset so the visible glyphs
            // (not the box) land on the line.
            var current = obj.getCenterPoint();
            var targetX = snapX - info.textOffset;
            obj.setPositionByOrigin(new fabric.Point(targetX, current.y), 'center', 'center');
            obj.setCoords();
            obj.lockMovementX = true;
            snapState = {
              obj:           obj,
              snapX:         snapX,
              snapIdx:       i,
              anchorMouseX:  mouseX != null ? mouseX : snapX,
            };
            break;
          }
        }
      }

      // ----- Y-axis engagement -----
      // Independent of X — uses the object's bounding-box vertical
      // centre. We don't bother with a "visible centre Y" adjustment
      // (equivalent to the X-axis textOffset) because text font
      // metrics mostly self-balance vertically: ascenders and
      // descenders extend roughly equal amounts above the cap-line
      // and below the baseline, so the bounding box's Y centre is a
      // close enough match to the visual centre for what customers
      // perceive as "centred". If we get feedback that capital-only
      // text feels slightly low (no descenders, so visual mass is
      // above box-centre), we can add a font-metric-based offset
      // here mirroring measureTextInkOffset for X.
      if (!snapStateY || snapStateY.obj !== obj) {
        var bounds  = obj.getBoundingRect();
        var centerY = bounds.top + bounds.height / 2;
        for (var j = 0; j < GUIDELINE_Y_FRACS.length; j++) {
          if (j === releasedFromIdxY) continue;
          var snapY  = getGuidelineCentreY(j);
          var diffY  = centerY - snapY;
          if (Math.abs(diffY) < SNAP_THRESHOLD_PX) {
            // Re-fetch centre after any X-axis snap above (which may
            // have just shifted obj.left). Y-target stays at snapY.
            var currentY = obj.getCenterPoint();
            obj.setPositionByOrigin(new fabric.Point(currentY.x, snapY), 'center', 'center');
            obj.setCoords();
            obj.lockMovementY = true;
            snapStateY = {
              obj:           obj,
              snapY:         snapY,
              snapIdx:       j,
              anchorMouseY:  mouseY != null ? mouseY : snapY,
            };
            break;
          }
        }
      }
    });

    // Release-detection lives on mouse:move — fires for every cursor
    // movement regardless of whether the locked object can actually
    // move on X. (object:moving wouldn't fire when X is locked and the
    // user drags purely horizontally, leaving the snap stuck forever.)
    //
    // When releasing mid-drag, we ALSO update Fabric's internal
    // _currentTransform.offsetX so post-release mouse-tracking continues
    // smoothly from the snapped position. Without this, Fabric would
    // recompute obj.left from the original drag-start position + total
    // mouse delta — jumping the object to "where it would have been
    // without the snap", which is offset by the engage-time correction.
    fabricCanvas.on('mouse:move', function (e) {
      // Both axes share an early-exit if nothing's locked.
      if (!snapState && !snapStateY) return;
      if (!fabricCanvas._currentTransform) return;
      var pointer = fabricCanvas.getPointer(e.e);

      // ----- X-axis release -----
      if (snapState && snapState.obj &&
          Math.abs(pointer.x - snapState.anchorMouseX) > SNAP_RELEASE_PX) {
        var t = fabricCanvas._currentTransform;
        if (t) {
          // Re-anchor the drag's offset so the next obj.left calc
          //   newLeft = pointer.x - transform.offsetX
          // yields the current (snapped) obj.left, not the original
          // mouse-tracked position. The object then resumes following
          // the cursor from where it is, rather than jumping.
          t.offsetX = pointer.x - snapState.obj.left;
        }
        snapState.obj.lockMovementX = false;
        releasedFromIdx = snapState.snapIdx;  // skip this guideline for rest of drag
        snapState = null;
      }

      // ----- Y-axis release -----
      // Mirror of the X-axis pattern: re-anchor offsetY so post-release
      // mouse-tracking continues smoothly from the snapped Y position
      // rather than jumping to where the object would have been
      // without the snap.
      if (snapStateY && snapStateY.obj &&
          Math.abs(pointer.y - snapStateY.anchorMouseY) > SNAP_RELEASE_PX) {
        var ty = fabricCanvas._currentTransform;
        if (ty) {
          ty.offsetY = pointer.y - snapStateY.obj.top;
        }
        snapStateY.obj.lockMovementY = false;
        releasedFromIdxY = snapStateY.snapIdx;
        snapStateY = null;
      }
    });

    // Reset the snap lock + released-line tracking at the end of a drag.
    // Next drag is unrestricted: any guideline (X or Y) can engage again.
    fabricCanvas.on('mouse:up', function () {
      if (snapState && snapState.obj) {
        snapState.obj.lockMovementX = false;
      }
      if (snapStateY && snapStateY.obj) {
        snapStateY.obj.lockMovementY = false;
      }
      snapState        = null;
      snapStateY       = null;
      releasedFromIdx  = null;
      releasedFromIdxY = null;
    });

    // After a user object is added, bring guidelines back to the top of
    // the stack so they remain visible above user content.
    fabricCanvas.on('object:added', function (e) {
      var obj = e.target;
      if (obj.data && obj.data.role === 'guideline') return;
      bringGuidelinesToFront();
    });

    // ---- Print-safe zone ----------------------------------------------
    // A non-interactive rectangle inset from canvas edges showing the
    // print-safe area. Customer can drop objects outside (clipped), but
    // anything inside this rectangle is guaranteed to print.
    // Print team (Danni, 4 May 2026): remove the visible bleed/safe-zone
    // rectangle — print process accounts for bleed already, customers
    // can place objects flush to the canvas edge. drawSafeZone() is
    // kept as a no-op so the (many) callers above don't have to be
    // touched; just nothing gets drawn. If we ever want it back, set
    // FF_SHOW_SAFE_ZONE = true.
    var FF_SHOW_SAFE_ZONE = false;
    var safeZone = null;
    function drawSafeZone() {
      // Always wipe an existing one (e.g. if the flag flips at runtime).
      if (safeZone) {
        fabricCanvas.remove(safeZone);
        safeZone = null;
      }
      if (!FF_SHOW_SAFE_ZONE) return;
      var margin = CONFIG.safe_margin || 0.04;
      var w = fabricCanvas.getWidth();
      var h = fabricCanvas.getHeight();
      safeZone = new fabric.Rect({
        left:   w * margin,
        top:    h * margin,
        width:  w * (1 - margin * 2),
        height: h * (1 - margin * 2),
        fill: 'transparent',
        stroke: 'rgba(199, 91, 18, 0.4)',
        strokeDashArray: [6, 4],
        strokeWidth: 1,
        selectable: false,
        evented:    false,
        hoverCursor: 'default',
        excludeFromExport: true,
      });
      safeZone.data = { role: 'safe-zone' };
      fabricCanvas.add(safeZone);
      // Always at the back so user objects render on top.
      fabricCanvas.sendToBack(safeZone);
    }
    drawSafeZone();
    drawGuidelines();

    // ============================================================
    // Toolbar actions
    // ============================================================

    // ---- Add photo -----------------------------------------------------
    //
    // Two paths depending on whether Uploadcare is configured:
    //
    //   1. Uploadcare (preferred): cloud sources (Google Drive, Dropbox,
    //      Facebook, Instagram, etc.) + camera + local. Returns a CDN
    //      URL — stored in the canvas state instead of a base64 data URI,
    //      so localStorage and the line-item-property JSON stay small
    //      even with multiple high-res photos.
    //
    //   2. Native file input (fallback): local-only, base64-encoded.
    //      Used when no uploadcare_public_key is set on the template.
    //      Functional but limits cloud sources and bloats stored state.

    if (ucUploader || ucCtx) {
      // Uploadcare flow.
      //
      // We render a custom orange "Add photo" button that matches the
      // rest of the toolbar (Add text etc.) and hide Uploadcare's stock
      // grey button. Click → call initFlow() on the ctx provider's API
      // to open Uploadcare's upload modal (with cloud sources + crop
      // editor). The hidden <uc-file-uploader-regular> still owns the
      // modal flow internally — we just don't show its launcher.
      if (addPhotoBtn) {
        addPhotoBtn.addEventListener('click', function () {
          // Try several paths to open the upload modal. Different
          // versions/builds of Uploadcare File Uploader expose the
          // trigger in different places — be robust.
          var opened = false;

          // 1. ctx provider's API (canonical v1 path).
          if (!opened && ucCtx && typeof ucCtx.getAPI === 'function') {
            try {
              var api = ucCtx.getAPI();
              if (api && typeof api.then === 'function') {
                api.then(function (a) {
                  if (a && typeof a.initFlow === 'function') a.initFlow();
                });
                opened = true;
              } else if (api && typeof api.initFlow === 'function') {
                api.initFlow();
                opened = true;
              } else {
                console.log('[ff-editor] ctx API has no initFlow:', api);
              }
            } catch (e) {
              console.warn('[ff-editor] ctx.getAPI threw:', e);
            }
          }

          // 2. file-uploader element's own API.
          if (!opened && ucUploader && typeof ucUploader.getAPI === 'function') {
            try {
              var uapi = ucUploader.getAPI();
              if (uapi && typeof uapi.initFlow === 'function') {
                uapi.initFlow();
                opened = true;
              }
            } catch (e) {
              console.warn('[ff-editor] uploader.getAPI threw:', e);
            }
          }

          // 3. Click the uploader element directly. Its shadow-DOM
          //    button responds to host-level click in some versions.
          if (!opened && ucUploader) {
            try {
              ucUploader.click();
              opened = true;
            } catch (e) {
              console.warn('[ff-editor] uploader.click() threw:', e);
            }
          }

          if (!opened) {
            console.error('[ff-editor] Could not open Uploadcare flow — none of the API paths worked. ucCtx:', ucCtx, 'ucUploader:', ucUploader);
          }
        });
      }

      // Event listeners — v1 dispatches events on the
      // <uc-upload-ctx-provider> element (the file-uploader element
      // doesn't always bubble). Listen on both to be safe — also on
      // window as a final fallback in case API changes.
      var processedUuids = {};

      function handleUploadcareSuccess(event) {
        var detail = event.detail || {};
        // v1 event detail shape: { uuid, cdnUrl, name, size, ... }
        // Some events (change) carry `allEntries` array instead.
        var entries = detail.allEntries || (detail.cdnUrl ? [detail] : []);
        if (!entries.length) {
          // Try other shapes — some versions use detail.fileInfo
          if (detail.fileInfo && detail.fileInfo.cdnUrl) entries = [detail.fileInfo];
        }
        entries.forEach(function (entry) {
          if (!entry || !entry.cdnUrl) return;
          // Skip if we've already processed this file (events can fire
          // multiple times — e.g. file-upload-success AND change AND
          // common-upload-success for the same upload).
          if (entry.uuid && processedUuids[entry.uuid]) return;
          if (entry.uuid) processedUuids[entry.uuid] = true;

          var baseUrl = entry.cdnUrl;
          // Canvas-resolution URL (1600px wide via Uploadcare's URL
          // operators) — Fabric stays snappy. Print recreator fetches
          // the bare baseUrl for full resolution.
          var canvasUrl = baseUrl + (baseUrl.charAt(baseUrl.length - 1) === '/' ? '' : '/') +
                          '-/preview/1600x1600/-/format/auto/-/quality/smart/';
          addPhotoFromUrl(canvasUrl, {
            role: 'user-image',
            filename: entry.name,
            uploadcareUuid: entry.uuid,
            uploadcareCdnUrl: baseUrl,
          });
        });
      }

      // Try every plausible target × event combination so we cover the
      // way the package actually dispatches events on this CDN build.
      // Each handler is idempotent (de-duplicated by UUID) so multiple
      // firings for the same upload are safe.
      var targets = [ucCtx, ucUploader].filter(Boolean);
      var events  = ['file-upload-success', 'common-upload-success', 'change', 'data-change'];
      targets.forEach(function (t) {
        events.forEach(function (ev) {
          t.addEventListener(ev, handleUploadcareSuccess);
        });
      });
    } else if (addPhotoBtn && photoInput) {
      addPhotoBtn.addEventListener('click', function () {
        photoInput.click();
      });
      photoInput.addEventListener('change', function (event) {
        var files = Array.from(event.target.files || []);
        files.forEach(function (file) { addPhotoFromFile(file); });
        photoInput.value = '';
      });
    }

    function addPhotoFromFile(file) {
      if (!file.type.startsWith('image/')) return;
      var reader = new FileReader();
      reader.onload = function (e) {
        addPhotoFromUrl(e.target.result, {
          role: 'user-image',
          filename: file.name,
        });
      };
      reader.readAsDataURL(file);
    }

    // Shared add-image path. URL can be a CDN URL (Uploadcare) or a
    // base64 data URI (native fallback). dataExtras is merged into
    // img.data for later serialisation.
    function addPhotoFromUrl(url, dataExtras) {
      fabric.Image.fromURL(url, function (img) {
        var maxW = fabricCanvas.getWidth() * 0.4;
        var scale = maxW / img.width;
        img.set({
          left:   fabricCanvas.getWidth()  / 2,
          top:    fabricCanvas.getHeight() / 2,
          originX: 'center',
          originY: 'center',
          scaleX: scale,
          scaleY: scale,
        });
        img.data = Object.assign({ role: 'user-image' }, dataExtras || {});
        fabricCanvas.add(img);
        fabricCanvas.setActiveObject(img);
        fabricCanvas.requestRenderAll();
        saveState();
      }, { crossOrigin: 'anonymous' });
    }

    // ---- Add text ------------------------------------------------------
    addTextBtn.addEventListener('click', function () {
      var text = new fabric.IText('Your text', {
        left:   fabricCanvas.getWidth()  / 2,
        top:    fabricCanvas.getHeight() / 2,
        originX: 'center',
        originY: 'center',
        fontFamily: slugToFontFamily(FONTS[0]),
        fontSize: 48,
        fill: '#111111',
        textAlign: 'center',
        editable: true,
      });
      text.data = { role: 'user-text', fontSlug: FONTS[0] };
      fabricCanvas.add(text);
      fabricCanvas.setActiveObject(text);
      text.enterEditing();
      text.selectAll();
      fabricCanvas.requestRenderAll();
      saveState();
    });

    // ---- Stickers (Iconify) -------------------------------------------
    // Searches the public Iconify API (no key required) for icons in
    // emoji + decorative sets, displays a grid of thumbnails, and
    // inserts the chosen icon as an SVG so it stays crisp at print
    // resolution. Same flow as Fabric photos for everything afterwards
    // — drag, resize, layer, save, 3D preview, print recreator all work
    // without any extra plumbing.
    if (addStickerBtn && stickerModal && stickerResults) {
      // Limit Iconify search to colourful / decorative sets — the full
      // Iconify catalog includes line-art icon sets which look sparse
      // on a mug. These prefixes give us emoji-style colour stickers.
      var STICKER_PREFIXES = 'twemoji,noto,fxemoji,openmoji,emojione,fluent-emoji-flat';
      var stickerSearchAbort = null;

      function openStickerModal(initialQuery) {
        stickerModal.hidden = false;
        stickerInput.value = '';
        if (initialQuery) {
          runStickerSearch(initialQuery);
        } else {
          stickerResults.innerHTML = '<p class="ff-sticker-empty">Pick a category above or type to search.</p>';
        }
        setTimeout(function () { stickerInput.focus(); }, 50);
      }
      function closeStickerModal() {
        stickerModal.hidden = true;
        if (stickerSearchAbort) {
          stickerSearchAbort.abort();
          stickerSearchAbort = null;
        }
      }
      addStickerBtn.addEventListener('click', function () {
        // Open with a pleasant default — hearts. Customer immediately
        // sees colourful options instead of an empty search box.
        openStickerModal('heart');
        markActiveChip('heart');
      });
      stickerCloseBtns.forEach(function (b) {
        b.addEventListener('click', closeStickerModal);
      });
      // Escape closes too.
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !stickerModal.hidden) closeStickerModal();
      });

      function markActiveChip(query) {
        stickerChips.forEach(function (c) {
          c.removeAttribute('data-active');
          if (c.getAttribute('data-q') === query) c.setAttribute('data-active', 'true');
        });
      }
      stickerChips.forEach(function (chip) {
        chip.addEventListener('click', function () {
          var q = chip.getAttribute('data-q');
          stickerInput.value = q;
          runStickerSearch(q);
          markActiveChip(q);
        });
      });

      // Debounced search on typing.
      var typeTimer = null;
      stickerInput.addEventListener('input', function () {
        var q = stickerInput.value.trim();
        markActiveChip(null);
        clearTimeout(typeTimer);
        typeTimer = setTimeout(function () {
          if (q.length >= 2) runStickerSearch(q);
          else stickerResults.innerHTML = '<p class="ff-sticker-empty">Keep typing…</p>';
        }, 250);
      });

      function runStickerSearch(query) {
        if (stickerSearchAbort) stickerSearchAbort.abort();
        stickerSearchAbort = new AbortController();
        stickerResults.innerHTML = '<p class="ff-sticker-loading">Searching…</p>';
        var url = 'https://api.iconify.design/search?query=' +
                  encodeURIComponent(query) +
                  '&limit=64&prefixes=' + STICKER_PREFIXES;
        fetch(url, { signal: stickerSearchAbort.signal })
          .then(function (r) { return r.json(); })
          .then(function (data) {
            renderStickerResults(data.icons || []);
          })
          .catch(function (err) {
            if (err.name === 'AbortError') return;
            console.error('[ff-editor] sticker search failed:', err);
            stickerResults.innerHTML = '<p class="ff-sticker-empty">Search failed — try again.</p>';
          });
      }

      function renderStickerResults(icons) {
        if (!icons.length) {
          stickerResults.innerHTML = '<p class="ff-sticker-empty">No stickers match that search.</p>';
          return;
        }
        var grid = document.createElement('div');
        grid.className = 'ff-sticker-grid';
        icons.forEach(function (name) {
          // name is "prefix:icon-name" — Iconify URL is /prefix/icon-name.svg
          var parts = name.split(':');
          if (parts.length !== 2) return;
          var thumbUrl = 'https://api.iconify.design/' + parts[0] + '/' + parts[1] + '.svg?width=64';
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.title = name;
          btn.innerHTML = '<img src="' + thumbUrl + '" alt="" loading="lazy">';
          btn.addEventListener('click', function () {
            insertSticker(parts[0], parts[1]);
          });
          grid.appendChild(btn);
        });
        stickerResults.innerHTML = '';
        stickerResults.appendChild(grid);
      }

      function insertSticker(prefix, icon) {
        // Load the FULL-resolution SVG, not the 64px thumb — print
        // resolution is what matters. Fabric loadSVGFromURL parses it
        // into objects we can group + add to the canvas.
        var svgUrl = 'https://api.iconify.design/' + prefix + '/' + icon + '.svg?width=512';
        fabric.loadSVGFromURL(svgUrl, function (objects, options) {
          if (!objects || !objects.length) {
            console.warn('[ff-editor] sticker SVG empty:', svgUrl);
            return;
          }
          var sticker = fabric.util.groupSVGElements(objects, options);
          // Default size: ~25% of canvas width; centre the sticker.
          var canvasW = fabricCanvas.getWidth();
          var canvasH = fabricCanvas.getHeight();
          var maxDim = Math.max(sticker.width || 1, sticker.height || 1);
          var targetSize = canvasW * 0.25;
          var s = targetSize / maxDim;
          sticker.set({
            left:   canvasW / 2,
            top:    canvasH / 2,
            originX: 'center',
            originY: 'center',
            scaleX: s,
            scaleY: s,
          });
          sticker.data = { role: 'user-sticker', iconName: prefix + ':' + icon };
          fabricCanvas.add(sticker);
          fabricCanvas.setActiveObject(sticker);
          fabricCanvas.requestRenderAll();
          saveState();
          closeStickerModal();
        });
      }
    }

    // ---- Background picker -------------------------------------------
    // Three flavours of background: none / solid colour / pattern.
    // State lives on the Fabric canvas (backgroundColor /
    // backgroundImage), so it serialises into _canvas_data and the
    // print recreator restores it automatically.
    if (addBgBtn && bgModal && bgSwatchesEl && bgPatternsEl) {
      var BG_SWATCHES = [
        { color: '',          label: 'No background', transparent: true },
        { color: '#ffffff',   label: 'White' },
        { color: '#f5f5f5',   label: 'Light grey' },
        { color: '#000000',   label: 'Black' },
        { color: '#fde4e1',   label: 'Blush' },
        { color: '#fff3c4',   label: 'Cream' },
        { color: '#e3f2fd',   label: 'Sky blue' },
        { color: '#dcedc8',   label: 'Mint' },
        { color: '#e1bee7',   label: 'Lavender' },
        { color: '#ffccbc',   label: 'Peach' },
        { color: '#c75b12',   label: 'Brand orange' },
        { color: '#5b3a29',   label: 'Brown' },
        { color: '#1a237e',   label: 'Navy' },
        { color: '#b71c1c',   label: 'Crimson' },
      ];
      // Inline SVG patterns. Each tile is small (20-60px viewBox) and
      // designed to repeat seamlessly. Fill colours are muted so
      // designs placed on top stay legible. utf8 dataURI is smaller
      // than base64 + readable in DevTools.
      //
      // To add more patterns: just push another object onto this
      // array with `name` + `svg` (string). They auto-render in the
      // picker grid in array order.
      var BG_PATTERNS = [
        // ===== Hearts =====
        { name: 'Big hearts',
          svg: '<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">' +
               '<rect width="40" height="40" fill="#fff5f7"/>' +
               '<path d="M20 28 C12 22 12 14 17 14 C18.5 14 20 15 20 16.5 C20 15 21.5 14 23 14 C28 14 28 22 20 28Z" fill="#e91e63"/>' +
               '</svg>' },
        { name: 'Tiny hearts',
          svg: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">' +
               '<rect width="20" height="20" fill="#fff5f7"/>' +
               '<path d="M10 14 C6 11 6 7 8.5 7 C9.3 7 10 7.6 10 8.5 C10 7.6 10.7 7 11.5 7 C14 7 14 11 10 14Z" fill="#f06292"/>' +
               '</svg>' },
        { name: 'Mixed hearts',
          svg: '<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 60 60">' +
               '<rect width="60" height="60" fill="#ffffff"/>' +
               '<path d="M15 22 C9 17 9 11 13 11 C14.5 11 15 12 15 13 C15 12 15.5 11 17 11 C21 11 21 17 15 22Z" fill="#f48fb1"/>' +
               '<path d="M45 50 C39 45 39 39 43 39 C44.5 39 45 40 45 41 C45 40 45.5 39 47 39 C51 39 51 45 45 50Z" fill="#90caf9"/>' +
               '<path d="M45 12 C40 8 40 4 43 4 C44 4 45 4.5 45 5.5 C45 4.5 46 4 47 4 C50 4 50 8 45 12Z" fill="#ce93d8"/>' +
               '</svg>' },

        // ===== Stars & sparkles =====
        { name: 'Big stars',
          svg: '<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">' +
               '<rect width="40" height="40" fill="#fffdf2"/>' +
               '<path d="M20 8 L22.5 16 L31 16 L24 21 L26.5 29 L20 24 L13.5 29 L16 21 L9 16 L17.5 16 Z" fill="#fbc02d"/>' +
               '</svg>' },
        { name: 'Sparkles',
          svg: '<svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50">' +
               '<rect width="50" height="50" fill="#0d1b2a"/>' +
               '<path d="M10 10 L11 13 L14 14 L11 15 L10 18 L9 15 L6 14 L9 13Z" fill="#fff59d"/>' +
               '<path d="M35 25 L36 28 L39 29 L36 30 L35 33 L34 30 L31 29 L34 28Z" fill="#fff59d"/>' +
               '<path d="M22 40 L22.7 42 L24.7 42.7 L22.7 43.4 L22 45.4 L21.3 43.4 L19.3 42.7 L21.3 42Z" fill="#fff59d"/>' +
               '<circle cx="40" cy="8" r="0.8" fill="#ffffff"/>' +
               '<circle cx="6" cy="35" r="0.8" fill="#ffffff"/>' +
               '<circle cx="46" cy="42" r="0.8" fill="#ffffff"/>' +
               '</svg>' },

        // ===== Dots =====
        { name: 'Polka dots',
          svg: '<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">' +
               '<rect width="40" height="40" fill="#fce4ec"/>' +
               '<circle cx="10" cy="10" r="3" fill="#ec407a"/>' +
               '<circle cx="30" cy="30" r="3" fill="#ec407a"/>' +
               '</svg>' },
        { name: 'Tiny dots',
          svg: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">' +
               '<rect width="20" height="20" fill="#fafafa"/>' +
               '<circle cx="10" cy="10" r="1" fill="#9e9e9e"/>' +
               '</svg>' },
        { name: 'Scattered dots',
          svg: '<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 60 60">' +
               '<rect width="60" height="60" fill="#e3f2fd"/>' +
               '<circle cx="10" cy="15" r="2.5" fill="#42a5f5"/>' +
               '<circle cx="35" cy="8" r="1.5" fill="#42a5f5"/>' +
               '<circle cx="50" cy="30" r="3" fill="#42a5f5"/>' +
               '<circle cx="20" cy="40" r="2" fill="#42a5f5"/>' +
               '<circle cx="45" cy="50" r="1.8" fill="#42a5f5"/>' +
               '</svg>' },

        // ===== Stripes =====
        { name: 'Stripes',
          svg: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">' +
               '<rect width="20" height="20" fill="#ffffff"/>' +
               '<rect x="0" width="10" height="20" fill="#e3f2fd"/>' +
               '</svg>' },
        { name: 'Pastel stripes',
          svg: '<svg xmlns="http://www.w3.org/2000/svg" width="60" height="20" viewBox="0 0 60 20">' +
               '<rect width="10" height="20" fill="#ffcdd2"/>' +
               '<rect x="10" width="10" height="20" fill="#fff9c4"/>' +
               '<rect x="20" width="10" height="20" fill="#c8e6c9"/>' +
               '<rect x="30" width="10" height="20" fill="#bbdefb"/>' +
               '<rect x="40" width="10" height="20" fill="#e1bee7"/>' +
               '<rect x="50" width="10" height="20" fill="#ffe0b2"/>' +
               '</svg>' },
        { name: 'Diagonal lines',
          svg: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">' +
               '<rect width="20" height="20" fill="#f5f5f5"/>' +
               '<path d="M-5 5 L5 -5 M0 20 L20 0 M15 25 L25 15" stroke="#bdbdbd" stroke-width="1.5"/>' +
               '</svg>' },

        // ===== Geometric =====
        { name: 'Triangles',
          svg: '<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">' +
               '<rect width="40" height="40" fill="#e8f5e9"/>' +
               '<path d="M20 8 L28 24 L12 24 Z" fill="#66bb6a"/>' +
               '</svg>' },
        { name: 'Diamonds',
          svg: '<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30">' +
               '<rect width="30" height="30" fill="#e8eaf6"/>' +
               '<path d="M15 5 L25 15 L15 25 L5 15 Z" fill="#7986cb"/>' +
               '</svg>' },
        { name: 'Squares',
          svg: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">' +
               '<rect width="20" height="20" fill="#f5f5f5"/>' +
               '<rect x="2" y="2" width="6" height="6" fill="#90caf9"/>' +
               '<rect x="12" y="12" width="6" height="6" fill="#90caf9"/>' +
               '</svg>' },
        { name: 'Plus signs',
          svg: '<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">' +
               '<rect width="40" height="40" fill="#fff8e1"/>' +
               '<path d="M18 10 H22 V18 H30 V22 H22 V30 H18 V22 H10 V18 H18 Z" fill="#fbc02d"/>' +
               '</svg>' },
        { name: 'Crosshatch',
          svg: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">' +
               '<rect width="20" height="20" fill="#ffffff"/>' +
               '<path d="M0 0 L20 20 M0 20 L20 0" stroke="#cfd8dc" stroke-width="0.8"/>' +
               '</svg>' },
        { name: 'Hexagons',
          svg: '<svg xmlns="http://www.w3.org/2000/svg" width="36" height="32" viewBox="0 0 36 32">' +
               '<rect width="36" height="32" fill="#f3e5f5"/>' +
               '<path d="M9 0 L18 0 L22.5 8 L18 16 L9 16 L4.5 8 Z M27 16 L36 16 L40.5 24 L36 32 L27 32 L22.5 24 Z M-9 16 L0 16 L4.5 24 L0 32 L-9 32 L-13.5 24 Z" fill="none" stroke="#9c27b0" stroke-width="1.2"/>' +
               '</svg>' },

        // ===== Linear motion =====
        { name: 'Chevrons',
          svg: '<svg xmlns="http://www.w3.org/2000/svg" width="40" height="20" viewBox="0 0 40 20">' +
               '<rect width="40" height="20" fill="#f1f8e9"/>' +
               '<path d="M0 8 L10 0 L20 8 L30 0 L40 8 V12 L30 4 L20 12 L10 4 L0 12 Z" fill="#7cb342"/>' +
               '</svg>' },
        { name: 'Zigzag',
          svg: '<svg xmlns="http://www.w3.org/2000/svg" width="30" height="20" viewBox="0 0 30 20">' +
               '<rect width="30" height="20" fill="#fff3e0"/>' +
               '<path d="M0 10 L7.5 4 L15 10 L22.5 4 L30 10" fill="none" stroke="#ff7043" stroke-width="2" stroke-linejoin="round"/>' +
               '</svg>' },
        { name: 'Waves',
          svg: '<svg xmlns="http://www.w3.org/2000/svg" width="40" height="20" viewBox="0 0 40 20">' +
               '<rect width="40" height="20" fill="#e1f5fe"/>' +
               '<path d="M0 10 Q10 4 20 10 T40 10" fill="none" stroke="#29b6f6" stroke-width="1.5"/>' +
               '<path d="M0 15 Q10 9 20 15 T40 15" fill="none" stroke="#4fc3f7" stroke-width="1.2" opacity="0.6"/>' +
               '</svg>' },

        // ===== Florals & nature =====
        { name: 'Cherry blossoms',
          svg: '<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 60 60">' +
               '<rect width="60" height="60" fill="#fff5f9"/>' +
               '<g fill="#f8bbd0">' +
               '<circle cx="15" cy="15" r="3"/><circle cx="20" cy="13" r="3"/><circle cx="22" cy="18" r="3"/><circle cx="18" cy="22" r="3"/><circle cx="13" cy="20" r="3"/>' +
               '<circle cx="42" cy="42" r="3"/><circle cx="47" cy="40" r="3"/><circle cx="49" cy="45" r="3"/><circle cx="45" cy="49" r="3"/><circle cx="40" cy="47" r="3"/>' +
               '</g>' +
               '<circle cx="18" cy="18" r="1.5" fill="#fce4ec"/>' +
               '<circle cx="45" cy="45" r="1.5" fill="#fce4ec"/>' +
               '</svg>' },
        { name: 'Leaves',
          svg: '<svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50">' +
               '<rect width="50" height="50" fill="#f1f8e9"/>' +
               '<path d="M10 30 Q15 15 25 20 Q20 30 10 30Z" fill="#7cb342"/>' +
               '<path d="M30 35 Q35 20 45 25 Q40 35 30 35Z" fill="#9ccc65"/>' +
               '<path d="M5 10 Q10 0 15 5 Q12 10 5 10Z" fill="#aed581"/>' +
               '</svg>' },
        { name: 'Snowflakes',
          svg: '<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">' +
               '<rect width="40" height="40" fill="#e3f2fd"/>' +
               '<g stroke="#90caf9" stroke-width="1.2" stroke-linecap="round" fill="none">' +
               '<line x1="20" y1="6" x2="20" y2="34"/>' +
               '<line x1="6" y1="20" x2="34" y2="20"/>' +
               '<line x1="10" y1="10" x2="30" y2="30"/>' +
               '<line x1="10" y1="30" x2="30" y2="10"/>' +
               '<path d="M16 10 L20 6 L24 10 M16 30 L20 34 L24 30 M10 16 L6 20 L10 24 M30 16 L34 20 L30 24"/>' +
               '</g>' +
               '</svg>' },

        // ===== Themed =====
        { name: 'Coffee beans',
          svg: '<svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50">' +
               '<rect width="50" height="50" fill="#efebe9"/>' +
               '<g fill="#5d4037">' +
               '<ellipse cx="15" cy="15" rx="6" ry="9" transform="rotate(25 15 15)"/>' +
               '<ellipse cx="35" cy="35" rx="6" ry="9" transform="rotate(-15 35 35)"/>' +
               '</g>' +
               '<g stroke="#3e2723" stroke-width="0.8" fill="none">' +
               '<path d="M11 9 Q15 15 19 21" transform="rotate(25 15 15)"/>' +
               '<path d="M31 29 Q35 35 39 41" transform="rotate(-15 35 35)"/>' +
               '</g>' +
               '</svg>' },
        { name: 'Music notes',
          svg: '<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 60 60">' +
               '<rect width="60" height="60" fill="#f3e5f5"/>' +
               '<g fill="#6a1b9a">' +
               '<ellipse cx="14" cy="22" rx="4" ry="3"/>' +
               '<rect x="17" y="6" width="1.5" height="16"/>' +
               '<ellipse cx="42" cy="44" rx="4" ry="3"/>' +
               '<rect x="45" y="28" width="1.5" height="16"/>' +
               '</g>' +
               '<path d="M18 6 Q26 8 28 14" stroke="#6a1b9a" stroke-width="1.5" fill="none"/>' +
               '<path d="M46 28 Q54 30 56 36" stroke="#6a1b9a" stroke-width="1.5" fill="none"/>' +
               '</svg>' },
        { name: 'Bubbles',
          svg: '<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 60 60">' +
               '<rect width="60" height="60" fill="#e1f5fe"/>' +
               '<g fill="none" stroke="#4fc3f7" stroke-width="1.2">' +
               '<circle cx="12" cy="14" r="6"/>' +
               '<circle cx="40" cy="20" r="9"/>' +
               '<circle cx="20" cy="42" r="7"/>' +
               '<circle cx="48" cy="48" r="5"/>' +
               '</g>' +
               '<g fill="#b3e5fc" opacity="0.5">' +
               '<circle cx="9" cy="11" r="1.5"/>' +
               '<circle cx="36" cy="16" r="2"/>' +
               '<circle cx="17" cy="38" r="1.8"/>' +
               '</g>' +
               '</svg>' },

        // ===== Misc decorative =====
        { name: 'Confetti',
          svg: '<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 60 60">' +
               '<rect width="60" height="60" fill="#fffafa"/>' +
               '<rect x="10" y="10" width="6" height="2" fill="#e91e63" transform="rotate(20 13 11)"/>' +
               '<rect x="35" y="20" width="6" height="2" fill="#fbc02d" transform="rotate(-30 38 21)"/>' +
               '<rect x="20" y="40" width="6" height="2" fill="#42a5f5" transform="rotate(45 23 41)"/>' +
               '<rect x="45" y="45" width="6" height="2" fill="#66bb6a" transform="rotate(-15 48 46)"/>' +
               '<rect x="50" y="10" width="6" height="2" fill="#ab47bc" transform="rotate(60 53 11)"/>' +
               '<rect x="5" y="48" width="6" height="2" fill="#ff7043" transform="rotate(-45 8 49)"/>' +
               '</svg>' },
        { name: 'Brick',
          svg: '<svg xmlns="http://www.w3.org/2000/svg" width="60" height="40" viewBox="0 0 60 40">' +
               '<rect width="60" height="40" fill="#bf6e4f"/>' +
               '<g fill="none" stroke="#a85a3e" stroke-width="1.5">' +
               '<line x1="0" y1="20" x2="60" y2="20"/>' +
               '<line x1="20" y1="0" x2="20" y2="20"/>' +
               '<line x1="40" y1="0" x2="40" y2="20"/>' +
               '<line x1="10" y1="20" x2="10" y2="40"/>' +
               '<line x1="30" y1="20" x2="30" y2="40"/>' +
               '<line x1="50" y1="20" x2="50" y2="40"/>' +
               '</g>' +
               '</svg>' },
      ];

      function svgToDataUrl(svg) {
        return 'data:image/svg+xml;utf8,' + encodeURIComponent(svg);
      }

      function openBgModal() {
        bgModal.hidden = false;
        renderBgSwatches();
        renderBgPatterns();
        markActiveBgChoice();
      }
      function closeBgModal() { bgModal.hidden = true; }

      function renderBgSwatches() {
        bgSwatchesEl.innerHTML = '';
        BG_SWATCHES.forEach(function (sw) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'ff-bg-swatch' + (sw.transparent ? ' ff-bg-swatch--transparent' : '');
          btn.title = sw.label;
          btn.setAttribute('data-bg-color', sw.color);
          if (!sw.transparent) btn.style.backgroundColor = sw.color;
          btn.addEventListener('click', function () {
            applySolidBg(sw.color);
            markActiveBgChoice();
          });
          bgSwatchesEl.appendChild(btn);
        });
      }

      function renderBgPatterns() {
        bgPatternsEl.innerHTML = '';
        BG_PATTERNS.forEach(function (pat, idx) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'ff-bg-pattern';
          btn.title = pat.name;
          btn.setAttribute('data-bg-pattern-idx', idx);
          btn.style.backgroundImage = 'url(\'' + svgToDataUrl(pat.svg) + '\')';
          btn.addEventListener('click', function () {
            applyPatternBg(pat);
            markActiveBgChoice();
          });
          bgPatternsEl.appendChild(btn);
        });
      }

      function applySolidBg(color) {
        // Clear any pattern, set the colour. Empty string → transparent.
        fabricCanvas.backgroundImage = null;
        fabricCanvas.backgroundColor = color || '';
        fabricCanvas.requestRenderAll();
        saveState();
      }

      function applyPatternBg(pattern) {
        var dataUrl = svgToDataUrl(pattern.svg);
        // Use fabric.Image.fromURL to load, then set as backgroundImage.
        // crossOrigin doesn't matter for inline data URIs.
        fabric.Image.fromURL(dataUrl, function (img) {
          // Build a fabric.Pattern for tiling. Set as backgroundColor
          // (which can be either a string colour or a Pattern object
          // in Fabric — Pattern wins visually if both are present).
          var p = new fabric.Pattern({
            source: img.getElement(),
            repeat: 'repeat',
          });
          fabricCanvas.backgroundImage = null;
          fabricCanvas.backgroundColor = p;
          fabricCanvas.requestRenderAll();
          saveState();
        });
      }

      function markActiveBgChoice() {
        // Highlight whichever swatch matches current backgroundColor.
        // Patterns can't be diffed easily so we leave their state alone.
        var currentColor = (typeof fabricCanvas.backgroundColor === 'string')
          ? fabricCanvas.backgroundColor
          : '';
        bgSwatchesEl.querySelectorAll('.ff-bg-swatch').forEach(function (el) {
          var c = el.getAttribute('data-bg-color') || '';
          el.toggleAttribute('data-active', c === currentColor);
        });
      }

      addBgBtn.addEventListener('click', openBgModal);
      bgCloseBtns.forEach(function (b) { b.addEventListener('click', closeBgModal); });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !bgModal.hidden) closeBgModal();
      });

      // Custom hex input.
      function applyCustomHex() {
        var v = (bgCustomHex.value || '').trim();
        if (!v) return;
        if (v.charAt(0) !== '#') v = '#' + v;
        // Basic validation: accept 3-, 6-, or 8-char hex.
        if (!/^#[0-9a-fA-F]{3}([0-9a-fA-F]{3}([0-9a-fA-F]{2})?)?$/.test(v)) {
          alert('That doesn\'t look like a hex colour. Try e.g. #ff5500.');
          return;
        }
        applySolidBg(v);
        markActiveBgChoice();
      }
      if (bgCustomApply) bgCustomApply.addEventListener('click', applyCustomHex);
      if (bgCustomHex) {
        bgCustomHex.addEventListener('keydown', function (e) {
          if (e.key === 'Enter') { e.preventDefault(); applyCustomHex(); }
        });
      }

      // Remove background entirely.
      if (bgClearBtn) {
        bgClearBtn.addEventListener('click', function () {
          applySolidBg('');
          markActiveBgChoice();
        });
      }
    }

    // ---- Preview (full-size modal) ------------------------------------
    // Renders a static snapshot of the canvas in a modal at the largest
    // size that fits the viewport. Customer reviews their layout away
    // from the editor's chrome. Static export is fine — they're not
    // editing in the modal, just looking. Opens to print resolution
    // (multiplier scales the canvas up to its configured print pixel
    // size) so the modal shows what'll actually print.
    if (previewBtn) {
      previewBtn.addEventListener('click', function () {
        // Hide the safe-zone in the export so it doesn't appear in the
        // preview. excludeFromExport=true on the rect handles this.
        var multiplier = (CONFIG.canvas_width || fabricCanvas.getWidth()) / fabricCanvas.getWidth();
        var dataUrl = fabricCanvas.toDataURL({
          format:     'png',
          multiplier: Math.max(1, Math.min(multiplier, 4)),  // cap at 4x to avoid huge dataURLs
        });
        previewImg.src = dataUrl;
        previewModal.hidden = false;
        document.body.style.overflow = 'hidden';  // prevent background scroll
      });
    }
    // "See on mug" handler now lives in /assets/sp-personaliser-helpers.js
    // as a document-level click delegate so both free-form and full-
    // width-template products can use [data-ff-scroll-to-3d] buttons.
    function closePreview() {
      previewModal.hidden = true;
      previewImg.src = '';
      document.body.style.overflow = '';
    }
    previewCloseBtns.forEach(function (b) {
      b.addEventListener('click', closePreview);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !previewModal.hidden) closePreview();
    });

    // ---- Clear ---------------------------------------------------------
    clearBtn.addEventListener('click', function () {
      if (!confirm('Clear the entire canvas? This cannot be undone.')) return;
      fabricCanvas.getObjects().forEach(function (o) {
        if (!(o.data && o.data.role === 'safe-zone')) {
          fabricCanvas.remove(o);
        }
      });
      fabricCanvas.discardActiveObject();
      fabricCanvas.requestRenderAll();
      saveState();
    });

    // ============================================================
    // Properties panel
    // ============================================================
    fabricCanvas.on('selection:created', updatePanelForSelection);
    fabricCanvas.on('selection:updated', updatePanelForSelection);
    fabricCanvas.on('selection:cleared', function () {
      showPanel('empty');
    });

    function updatePanelForSelection() {
      var obj = fabricCanvas.getActiveObject();
      if (!obj || (obj.data && obj.data.role === 'safe-zone')) {
        showPanel('empty');
        return;
      }
      objectActions.hidden = false;
      if (obj.type === 'i-text' || obj.type === 'text') {
        showPanel('text');
        textFont.value    = (obj.data && obj.data.fontSlug) || FONTS[0];
        textSize.value    = Math.round(obj.fontSize);
        textSizeVal.textContent = Math.round(obj.fontSize);
        textColor.value   = rgbToHex(obj.fill);
        textBold.dataset.active   = (obj.fontWeight === 'bold') ? 'true' : 'false';
        textItalic.dataset.active = (obj.fontStyle === 'italic') ? 'true' : 'false';
        textAlignBtns.forEach(function (b) {
          b.dataset.active = (b.getAttribute('data-ff-align') === obj.textAlign) ? 'true' : 'false';
        });
        // Line + letter spacing — Fabric defaults are lineHeight=1.16,
        // charSpacing=0. Sync the sliders to the active text's values.
        if (textLineHeight) {
          var lh = obj.lineHeight != null ? obj.lineHeight : 1.16;
          textLineHeight.value = lh;
          if (textLineHeightVal) textLineHeightVal.textContent = lh.toFixed(2);
        }
        if (textCharSpacing) {
          var cs = obj.charSpacing != null ? obj.charSpacing : 0;
          textCharSpacing.value = cs;
          if (textCharSpacingVal) textCharSpacingVal.textContent = cs;
        }
      } else if (obj.type === 'image') {
        showPanel('image');
        imageOpacity.value = obj.opacity;
        imageOpacityVal.textContent = Math.round(obj.opacity * 100) + '%';
        updateLowResWarning(obj);
      } else if (obj.data && obj.data.role === 'user-sticker') {
        // Stickers are Fabric Group / Path objects — type doesn't
        // match 'image' or 'i-text'. Detect via the role tag set
        // when the sticker was inserted.
        showPanel('sticker');
        if (stickerOpacity) {
          var op = obj.opacity != null ? obj.opacity : 1;
          stickerOpacity.value = op;
          if (stickerOpacityVal) stickerOpacityVal.textContent = Math.round(op * 100) + '%';
        }
      }
    }

    // ----- Low-resolution photo warning -----
    //
    // We compare the photo's effective source pixels (post-crop) to
    // the print pixels needed for the canvas area it occupies, scaled
    // up to the LARGEST mug variant (20oz at 300 DPI = 3189 × 1359 px).
    // Using the largest variant means the warning is consistent
    // regardless of which size the customer has currently selected —
    // we surface a problem they'd hit IF they pick the biggest mug.
    //
    // 5% tolerance — being just slightly under the required pixel
    // count is fine in practice; customers don't notice 5% upscale.
    var LARGEST_PRINT_W_PX = 3189;
    var LARGEST_PRINT_H_PX = 1359;
    function updateLowResWarning(imgObj) {
      if (!lowResWarn) return;
      if (!imgObj || imgObj.type !== 'image') {
        lowResWarn.hidden = true;
        return;
      }
      // Source pixels actually visible (after any cropX/cropY/width/height).
      var srcW = imgObj.width  || 1;
      var srcH = imgObj.height || 1;
      // Canvas pixels the photo occupies right now (factoring in scale).
      var canvasOccupiedW = imgObj.getScaledWidth();
      var canvasOccupiedH = imgObj.getScaledHeight();
      // Print scale: editor canvas → largest print canvas. Use the
      // larger of width/height ratios so we're conservative for
      // either orientation.
      var fabricW = fabricCanvas.getWidth() || 1;
      var fabricH = fabricCanvas.getHeight() || 1;
      var scaleX = LARGEST_PRINT_W_PX / fabricW;
      var scaleY = LARGEST_PRINT_H_PX / fabricH;
      var requiredW = canvasOccupiedW * scaleX;
      var requiredH = canvasOccupiedH * scaleY;
      var isLowRes = srcW < requiredW * 0.95 || srcH < requiredH * 0.95;
      lowResWarn.hidden = !isLowRes;
    }
    // Re-check whenever the active photo gets resized (drag-end). Also
    // re-check after a crop is applied — both fire object:modified.
    fabricCanvas.on('object:modified', function (e) {
      var obj = e.target;
      if (obj && obj.type === 'image' && fabricCanvas.getActiveObject() === obj) {
        updateLowResWarning(obj);
      }
    });

    function showPanel(which) {
      emptyPanel.hidden = (which !== 'empty');
      textPanel.hidden  = (which !== 'text');
      imagePanel.hidden = (which !== 'image');
      if (stickerPanel) stickerPanel.hidden = (which !== 'sticker');
      if (cropPanel) cropPanel.hidden = (which !== 'crop');
      // Object actions hidden in crop mode (no front/back/duplicate while
      // adjusting a crop) and on the empty panel.
      objectActions.hidden = (which === 'empty' || which === 'crop');
    }

    // ---- Text panel handlers ------------------------------------------
    // (Text content is edited inline on the canvas via double-click —
    // no separate textarea. Font/size/colour/bold/italic still use
    // the panel inputs to act on the active object.)
    textFont.addEventListener('change', function () {
      var obj = fabricCanvas.getActiveObject();
      if (!obj) return;
      var slug = textFont.value;
      var family = FONT_FAMILY_MAP[slug] || slug;
      // Make sure the font is actually loaded before Fabric tries to
      // render with it — otherwise the canvas keeps showing the
      // fallback even though we've set fontFamily correctly.
      var apply = function () {
        obj.set({ fontFamily: slugToFontFamily(slug) });
        obj.data = obj.data || {};
        obj.data.fontSlug = slug;
        // Force Fabric to recompute text dimensions with the new font
        // metrics — without this, the bounding box stays sized for
        // the previous font and the visible text overflows.
        if (typeof obj.initDimensions === 'function') obj.initDimensions();
        obj.setCoords();
        fabricCanvas.requestRenderAll();
        saveState();
      };
      if (document.fonts && typeof document.fonts.load === 'function') {
        document.fonts.load("32px '" + family + "'")
          .then(apply, apply);   // apply on success or failure
      } else {
        apply();
      }
    });
    textSize.addEventListener('input', function () {
      var obj = fabricCanvas.getActiveObject();
      if (!obj) return;
      var size = parseInt(textSize.value, 10);
      obj.set({ fontSize: size });
      textSizeVal.textContent = size;
      fabricCanvas.requestRenderAll();
      saveState();
    });
    if (textLineHeight) {
      textLineHeight.addEventListener('input', function () {
        var obj = fabricCanvas.getActiveObject();
        if (!obj) return;
        var lh = parseFloat(textLineHeight.value);
        obj.set({ lineHeight: lh });
        if (textLineHeightVal) textLineHeightVal.textContent = lh.toFixed(2);
        fabricCanvas.requestRenderAll();
        saveState();
      });
    }
    if (textCharSpacing) {
      textCharSpacing.addEventListener('input', function () {
        var obj = fabricCanvas.getActiveObject();
        if (!obj) return;
        var cs = parseInt(textCharSpacing.value, 10);
        // Fabric uses 1/1000 em for charSpacing — bigger = looser,
        // negative = tighter. Set it directly; Fabric handles the
        // re-layout + re-render of glyph positions.
        obj.set({ charSpacing: cs });
        if (textCharSpacingVal) textCharSpacingVal.textContent = cs;
        fabricCanvas.requestRenderAll();
        saveState();
      });
    }
    textColor.addEventListener('input', function () {
      var obj = fabricCanvas.getActiveObject();
      if (!obj) return;
      obj.set({ fill: textColor.value });
      fabricCanvas.requestRenderAll();
      saveState();
    });
    textBold.addEventListener('click', function () {
      var obj = fabricCanvas.getActiveObject();
      if (!obj) return;
      var on = obj.fontWeight !== 'bold';
      obj.set({ fontWeight: on ? 'bold' : 'normal' });
      textBold.dataset.active = on ? 'true' : 'false';
      fabricCanvas.requestRenderAll();
      saveState();
    });
    textItalic.addEventListener('click', function () {
      var obj = fabricCanvas.getActiveObject();
      if (!obj) return;
      var on = obj.fontStyle !== 'italic';
      obj.set({ fontStyle: on ? 'italic' : 'normal' });
      textItalic.dataset.active = on ? 'true' : 'false';
      fabricCanvas.requestRenderAll();
      saveState();
    });
    textAlignBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var obj = fabricCanvas.getActiveObject();
        if (!obj) return;
        var align = btn.getAttribute('data-ff-align');
        obj.set({ textAlign: align });
        textAlignBtns.forEach(function (b) {
          b.dataset.active = (b === btn) ? 'true' : 'false';
        });
        fabricCanvas.requestRenderAll();
        saveState();
      });
    });

    // ---- Image panel handlers -----------------------------------------
    imageOpacity.addEventListener('input', function () {
      var obj = fabricCanvas.getActiveObject();
      if (!obj) return;
      var op = parseFloat(imageOpacity.value);
      obj.set({ opacity: op });
      imageOpacityVal.textContent = Math.round(op * 100) + '%';
      fabricCanvas.requestRenderAll();
      saveState();
    });
    if (stickerOpacity) {
      stickerOpacity.addEventListener('input', function () {
        var obj = fabricCanvas.getActiveObject();
        if (!obj) return;
        var op = parseFloat(stickerOpacity.value);
        obj.set({ opacity: op });
        if (stickerOpacityVal) stickerOpacityVal.textContent = Math.round(op * 100) + '%';
        fabricCanvas.requestRenderAll();
        saveState();
      });
    }
    // ---- Canvas-side cropping -----------------------------------------
    //
    // "Crop" button on the image properties panel enters a crop mode:
    //   - The image is locked (non-selectable) while in crop mode
    //   - A draggable + resizable rectangle appears over the image
    //   - Properties panel switches to the crop panel (Apply / Cancel)
    //   - Apply commits the new crop using Fabric's native cropX / cropY /
    //     width / height properties; image is repositioned to where the
    //     crop rect was so it appears in the same spot on the canvas.
    //   - Cancel discards the rect, restores image interaction.
    //
    // Customer can re-crop the same image any number of times — each
    // crop is relative to the currently-cropped portion (cropX / cropY
    // accumulate with each apply).
    var cropRect       = null;
    var cropTargetImg  = null;

    function startCropMode() {
      var img = fabricCanvas.getActiveObject();
      if (!img || img.type !== 'image') return;
      if (cropRect) cancelCropMode();  // exit any prior crop mode

      cropTargetImg = img;
      img.set({ selectable: false, evented: false });

      // Initial crop rect — slightly inset from the image bounds so the
      // customer can immediately see the affordance. Resize from there.
      var b = img.getBoundingRect();
      var inset = 0.08;
      cropRect = new fabric.Rect({
        left:   b.left + b.width  * inset,
        top:    b.top  + b.height * inset,
        width:  b.width  * (1 - inset * 2),
        height: b.height * (1 - inset * 2),
        fill: 'rgba(0,0,0,0)',
        stroke: '#c75b12',
        strokeDashArray: [8, 4],
        strokeWidth: 2,
        cornerColor: '#c75b12',
        cornerStyle: 'rect',
        transparentCorners: false,
        hasRotatingPoint: false,
        lockRotation: true,
        originX: 'left',
        originY: 'top',
        excludeFromExport: true,
      });
      cropRect.data = { role: 'crop-rect' };
      fabricCanvas.add(cropRect);
      fabricCanvas.setActiveObject(cropRect);
      fabricCanvas.requestRenderAll();
      showPanel('crop');
    }

    function applyCrop() {
      if (!cropRect || !cropTargetImg) return;
      var img = cropTargetImg;
      var rectBounds = cropRect.getBoundingRect();
      var imgBounds  = img.getBoundingRect();

      // Convert the rect's canvas-pixel position into source-pixel
      // offsets within the image's currently-cropped portion. Fabric's
      // image.width / .height are the source-pixel dimensions of the
      // visible (cropped) area; .scaleX / .scaleY scale that to display.
      var dispToSrcX = img.width  / imgBounds.width;
      var dispToSrcY = img.height / imgBounds.height;

      var srcDx = (rectBounds.left - imgBounds.left) * dispToSrcX;
      var srcDy = (rectBounds.top  - imgBounds.top ) * dispToSrcY;
      var srcW  =  rectBounds.width  * dispToSrcX;
      var srcH  =  rectBounds.height * dispToSrcY;

      img.set({
        cropX:  img.cropX + srcDx,
        cropY:  img.cropY + srcDy,
        width:  srcW,
        height: srcH,
      });

      // Reposition image to the crop rect's location. Account for the
      // image's origin (set to 'center' when added in addPhotoFromUrl).
      var newLeft, newTop;
      if (img.originX === 'center') newLeft = rectBounds.left + rectBounds.width  / 2;
      else                          newLeft = rectBounds.left;
      if (img.originY === 'center') newTop  = rectBounds.top  + rectBounds.height / 2;
      else                          newTop  = rectBounds.top;
      img.set({ left: newLeft, top: newTop });
      img.setCoords();

      // Cleanup crop rect + restore image interaction.
      fabricCanvas.remove(cropRect);
      cropRect = null;
      img.set({ selectable: true, evented: true });
      fabricCanvas.setActiveObject(img);
      cropTargetImg = null;

      fabricCanvas.requestRenderAll();
      saveState();
      // Active selection switches back to the image — selection:updated
      // event will fire and showPanel('image') will run.
    }

    function cancelCropMode() {
      if (!cropRect) {
        showPanel('image');
        return;
      }
      fabricCanvas.remove(cropRect);
      cropRect = null;
      if (cropTargetImg) {
        cropTargetImg.set({ selectable: true, evented: true });
        fabricCanvas.setActiveObject(cropTargetImg);
        cropTargetImg = null;
      }
      fabricCanvas.requestRenderAll();
      // Selection event handler will showPanel('image').
    }

    if (imageCropBtn) imageCropBtn.addEventListener('click', startCropMode);
    if (cropApplyBtn)  cropApplyBtn.addEventListener('click', applyCrop);
    if (cropCancelBtn) cropCancelBtn.addEventListener('click', cancelCropMode);

    if (imageReplaceBtn) {
      // Replace photo flow. With Uploadcare, replacement is non-trivial
      // (the uploader is shared across all photos) — for v1 we hide
      // the Replace button when Uploadcare is active and customers
      // delete + re-add. With the native fallback we use the file input
      // to swap the active image's src in place.
      if (!photoInput || ucUploader) {
        imageReplaceBtn.hidden = true;
      } else {
        imageReplaceBtn.addEventListener('click', function () {
          var origObj = fabricCanvas.getActiveObject();
          if (!origObj || origObj.type !== 'image') return;
          var oneShot = function (event) {
            var file = (event.target.files || [])[0];
            photoInput.removeEventListener('change', oneShot);
            photoInput.value = '';
            if (!file) return;
            var reader = new FileReader();
            reader.onload = function (e) {
              origObj.setSrc(e.target.result, function () {
                fabricCanvas.requestRenderAll();
                saveState();
              }, { crossOrigin: 'anonymous' });
              origObj.data = origObj.data || {};
              origObj.data.filename = file.name;
            };
            reader.readAsDataURL(file);
          };
          photoInput.addEventListener('change', oneShot);
          photoInput.click();
        });
      }
    }

    // ---- Object actions -----------------------------------------------
    bringForwardBtn.addEventListener('click', function () {
      var obj = fabricCanvas.getActiveObject();
      if (obj) { fabricCanvas.bringForward(obj); saveState(); }
    });
    sendBackwardBtn.addEventListener('click', function () {
      var obj = fabricCanvas.getActiveObject();
      if (obj) {
        fabricCanvas.sendBackwards(obj);
        // Make sure safe zone stays at the very back.
        if (safeZone) fabricCanvas.sendToBack(safeZone);
        saveState();
      }
    });
    duplicateBtn.addEventListener('click', function () {
      var obj = fabricCanvas.getActiveObject();
      if (!obj) return;
      obj.clone(function (clone) {
        clone.set({
          left: obj.left + 20,
          top:  obj.top  + 20,
        });
        if (obj.data) clone.data = JSON.parse(JSON.stringify(obj.data));
        fabricCanvas.add(clone);
        fabricCanvas.setActiveObject(clone);
        fabricCanvas.requestRenderAll();
        saveState();
      });
    });
    deleteBtn.addEventListener('click', function () {
      var obj = fabricCanvas.getActiveObject();
      if (!obj) return;
      fabricCanvas.remove(obj);
      fabricCanvas.discardActiveObject();
      fabricCanvas.requestRenderAll();
      saveState();
    });

    // Keyboard delete / backspace when canvas is focused.
    document.addEventListener('keydown', function (e) {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'Delete' || e.key === 'Backspace') {
        var obj = fabricCanvas.getActiveObject();
        if (!obj || obj.isEditing) return;
        e.preventDefault();
        fabricCanvas.remove(obj);
        saveState();
      }
    });

    // ============================================================
    // Undo / redo history
    // ============================================================
    //
    // Simple state-history approach. Every meaningful canvas mutation
    // pushes a serialised snapshot onto the history stack; undo/redo
    // navigate the stack and rehydrate via loadFromJSON.
    //
    // Coalescing: object:modified fires once per drag/resize (at the
    // mouseup), and once per text edit (at editing exit), so we don't
    // get spammy entries during continuous interactions. Inputs from
    // the properties panel push history on input commit (most use the
    // shared saveState path).
    //
    // The `applyingHistory` flag prevents loadFromJSON's own object:added
    // events from re-pushing the state we just restored.
    var historyStack = [];
    var historyIndex = -1;          // points at the "current" entry
    var historyLimit = 50;
    var applyingHistory = false;

    function pushHistory() {
      if (applyingHistory) return;
      var snap = serialiseCanvasForHistory();
      // Trim any redo-future when we make a new edit after undoing.
      if (historyIndex < historyStack.length - 1) {
        historyStack = historyStack.slice(0, historyIndex + 1);
      }
      historyStack.push(snap);
      if (historyStack.length > historyLimit) {
        historyStack.shift();
      } else {
        historyIndex = historyStack.length - 1;
      }
      historyIndex = historyStack.length - 1;
      updateUndoRedoButtons();
    }

    function serialiseCanvasForHistory() {
      // Stringified for cheap deep-clone + comparison if needed later.
      return JSON.stringify(fabricCanvas.toJSON(['data']));
    }

    function applyHistory(index) {
      if (index < 0 || index >= historyStack.length) return;
      historyIndex = index;
      applyingHistory = true;
      var snap = JSON.parse(historyStack[index]);
      fabricCanvas.loadFromJSON(snap, function () {
        // Safe-zone + guidelines are excluded from export, re-add after rehydrate.
        drawSafeZone();
        drawGuidelines();
        fabricCanvas.requestRenderAll();
        applyingHistory = false;
        // Persist the rehydrated state to localStorage + form input
        // (saveState skips history because applyingHistory is now false
        // — but we just set it false, so use a one-shot guard).
        saveStateNoHistory();
        updateUndoRedoButtons();
      });
    }

    function undo() {
      if (historyIndex > 0) applyHistory(historyIndex - 1);
    }
    function redo() {
      if (historyIndex < historyStack.length - 1) applyHistory(historyIndex + 1);
    }

    function updateUndoRedoButtons() {
      undoBtn.disabled = (historyIndex <= 0);
      redoBtn.disabled = (historyIndex >= historyStack.length - 1);
    }

    undoBtn.addEventListener('click', undo);
    redoBtn.addEventListener('click', redo);

    // Keyboard shortcuts — Cmd/Ctrl+Z (undo), Cmd/Ctrl+Shift+Z or Ctrl+Y (redo).
    // Skip when typing in form fields (textarea, input) to avoid
    // hijacking the customer's text editing. Inline canvas text editing
    // is also skipped (Fabric handles that itself).
    document.addEventListener('keydown', function (e) {
      var tag = (e.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      var active = fabricCanvas.getActiveObject();
      if (active && active.isEditing) return;
      var meta = e.metaKey || e.ctrlKey;
      if (meta && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault();
        if (e.shiftKey) redo(); else undo();
      } else if (meta && (e.key === 'y' || e.key === 'Y')) {
        e.preventDefault();
        redo();
      }
    });

    // ============================================================
    // Save / restore state
    // ============================================================
    var STORAGE_KEY = 'fb-free-form:' + (CONFIG.product_handle || 'unknown');

    // Canvas mutations: persist + push to undo history.
    // applyingHistory flag (set by applyHistory) suppresses history
    // pushes during rehydration so we don't loop.
    fabricCanvas.on('object:modified', saveState);
    fabricCanvas.on('object:added',    saveState);
    fabricCanvas.on('object:removed',  saveState);

    // ----- Default-image overlay toggle -----
    // The product page renders a default marketing image over the live
    // 3D preview until the customer adds their first piece of content.
    // The personaliser and free-form snippets share the same gallery
    // markup; the gallery's `fb-has-photos` class fades the overlay
    // out via CSS. We toggle it whenever the canvas's "user content"
    // count crosses 0 ↔ 1+ (anything that's NOT a guideline / safe-zone
    // / snap-debug-dot counts).
    function updateGalleryHasPhotosState() {
      var galleryEl = document.querySelector('[data-fb-photo-default]');
      if (!galleryEl) return;
      var hasUserContent = fabricCanvas.getObjects().some(function (o) {
        if (!o.data) return true;             // unflagged = user object
        var role = o.data.role;
        return role !== 'guideline'
            && role !== 'safe-zone'
            && role !== 'snap-debug-dot';
      });
      var alreadyOn = galleryEl.classList.contains('fb-has-photos');
      if (hasUserContent !== alreadyOn) {
        galleryEl.classList.toggle('fb-has-photos', hasUserContent);
      }
    }
    fabricCanvas.on('object:added',   updateGalleryHasPhotosState);
    fabricCanvas.on('object:removed', updateGalleryHasPhotosState);
    // Initial sync — restoreState may have rehydrated objects from
    // localStorage before any of these listeners were wired up.
    setTimeout(updateGalleryHasPhotosState, 0);

    // ============================================================
    // Add-to-cart gate — block submission when the canvas is empty.
    // ============================================================
    //
    // We had a customer order arrive with empty `_canvas_data` because
    // they hit Add to Cart before designing anything (or after clearing
    // the canvas). Without a design, Danni has nothing to print. Mirror
    // the pattern the photo templates use: disable [name="add"] +
    // surface a clear "design something first" note + block clicks in
    // the capture phase as a belt-and-braces against any dynamic
    // checkout button paths that bypass the disabled state.
    function isCanvasEmpty() {
      if (!fabricCanvas) return true;
      // Filter out internal overlay objects (safe-zone rectangle,
      // guidelines, snap-debug dots) — they're flagged
      // excludeFromExport so they don't end up in _canvas_data, but
      // they DO live in getObjects(). Without this filter the canvas
      // always reports ≥2 objects and the gate never fires on load.
      var userObjects = fabricCanvas.getObjects().filter(function (o) {
        return !o.excludeFromExport;
      });
      if (userObjects.length > 0) return false;
      // Background colour change counts as a design choice too — the
      // customer picked Red, that should print as Red even with no
      // text/photos on top. Compare against the template default.
      var defaultBg = (CONFIG && CONFIG.background_colour) || '#ffffff';
      var bg = fabricCanvas.backgroundColor;
      // Fabric stores backgroundColor as a string OR a Pattern object.
      // Patterns count as a design (chosen via the Background picker),
      // colours only count if changed from default.
      if (bg && typeof bg !== 'string') return false;
      if (bg && bg.toLowerCase() !== defaultBg.toLowerCase()) return false;
      return true;
    }

    var atcGateNote = null;
    function ensureAtcGateNote(buyArea) {
      if (!buyArea) return null;
      if (atcGateNote && atcGateNote.parentNode) return atcGateNote;
      var note = buyArea.querySelector('[data-ff-atc-gate-note]');
      if (note) { atcGateNote = note; return note; }
      note = document.createElement('p');
      note.className = 'fb-atc-gate-note';
      note.setAttribute('data-ff-atc-gate-note', '');
      buyArea.insertBefore(note, buyArea.firstChild);
      atcGateNote = note;
      return note;
    }

    function updateFreeFormAtcGate() {
      var empty = isCanvasEmpty();
      // Toggle a class on <body> instead of trying to set disabled
      // attributes on the button. The theme's product-form custom
      // element and the Loox listener (lb-atc-listener) actively re-
      // render or strip unknown attrs/classes off the button, which
      // killed our previous attempt. <body> is stable — nothing else
      // touches our custom class — so CSS can hang the visual + the
      // pointer-events block off it via a descendant selector.
      document.body.classList.toggle('ff-canvas-empty', empty);

      // Note above the relocated qty/ATC area inside the editor's
      // bottom strip. Falls back gracefully if the buy area hasn't
      // been laid out yet.
      var buyArea = document.querySelector('.ff-bottom-buy') ||
                    document.querySelector('.ff-bottom-atc');
      var note = ensureAtcGateNote(buyArea);
      if (note) {
        if (empty) {
          note.textContent = 'Design your mug above before adding to cart — pop a photo, text, or sticker on the canvas to get started.';
          note.hidden = false;
        } else {
          note.textContent = '';
          note.hidden = true;
        }
      }
    }

    // Capture-phase click interceptor — runs BEFORE the theme's
    // product-form submit handler. If the canvas is empty, swallow
    // the click and scroll the editor into view so the customer
    // sees what to do. Only matches BUTTON elements (Uploadcare's
    // <uc-icon name="add"> components also match the bare attribute
    // selector — those clicks are inside the uploader modal and
    // shouldn't be touched).
    document.addEventListener('click', function (e) {
      var target = e.target.closest && e.target.closest('button[name="add"]');
      if (!target) return;
      if (isCanvasEmpty()) {
        e.preventDefault();
        e.stopPropagation();
        updateFreeFormAtcGate();
        var editor = root || document.querySelector('[data-ff-editor]');
        if (editor && editor.scrollIntoView) {
          editor.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }, true);

    // Re-evaluate on every meaningful canvas change.
    fabricCanvas.on('object:added',   updateFreeFormAtcGate);
    fabricCanvas.on('object:removed', updateFreeFormAtcGate);
    // Background colour / pattern changes go through saveState — wrap
    // it once to also re-evaluate the gate.
    var origSaveStateForGate = saveState;
    saveState = function () {
      origSaveStateForGate.apply(null, arguments);
      updateFreeFormAtcGate();
    };

    // Initial evaluation — the relocator runs on DOMContentLoaded but
    // the personaliser-helpers script may load late. Retry a few times
    // at increasing delays so we catch the button regardless of when
    // it appears in the DOM. Idempotent — calling the gate repeatedly
    // is cheap.
    [0, 100, 300, 800, 1800].forEach(function (delay) {
      setTimeout(updateFreeFormAtcGate, delay);
    });
    // Also re-evaluate when the page becomes visible / interactive,
    // catches the case where the relocator finishes after our last retry.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', updateFreeFormAtcGate);
    }
    window.addEventListener('load', updateFreeFormAtcGate);

    // Belt-and-braces — if some other script is stripping our body
    // class (theme, plugin, anti-CSS-injection script), watch for
    // changes and re-apply. Only kicks in when we want the class
    // present (canvas empty); when not empty we don't care.
    if (window.MutationObserver) {
      var bodyClassObserver = new MutationObserver(function () {
        if (isCanvasEmpty() && !document.body.classList.contains('ff-canvas-empty')) {
          document.body.classList.add('ff-canvas-empty');
        }
      });
      bodyClassObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    // ----- Text resize: bake scale into fontSize on drag end -----
    //
    // Fabric default behaviour: dragging a text corner handle multiplies
    // scaleX/scaleY rather than changing fontSize. That's fine for the
    // visible result, but causes two bugs:
    //   1. The size slider reads fontSize, so it shows the original
    //      value (e.g. 48) even when the visible text is at scale 0.5
    //      = effectively 24px on screen.
    //   2. A subsequent slider drag then sets fontSize directly with
    //      the live scale still applied, producing wildly wrong sizes.
    //
    // We can't bake during scaling — Fabric tracks scale relative to
    // drag-start state, and resetting scale mid-drag causes runaway
    // compounding (we get 70-million-px text after a single drag).
    //
    // Instead: capture the pre-drag fontSize on scale-start, show a
    // LIVE effective size on the slider during drag, then bake into
    // fontSize ONCE on drag-end (object:modified).
    function isTextLike(obj) {
      return obj && (obj.type === 'i-text' || obj.type === 'text' || obj.type === 'textbox');
    }
    var textScaleStart = null;  // { fontSize } at drag start
    fabricCanvas.on('object:scaling', function (e) {
      var obj = e.target;
      if (!isTextLike(obj)) return;
      // Capture pre-drag fontSize on the first scaling tick.
      if (!textScaleStart) {
        textScaleStart = { fontSize: obj.fontSize };
      }
      // Live display of effective size. obj.scaleX is Fabric's drag-tracked
      // multiplier (1 at start, grows/shrinks as the customer drags).
      var liveSize = Math.max(4, Math.round(textScaleStart.fontSize * (obj.scaleX || 1)));
      if (fabricCanvas.getActiveObject() === obj && textSize && textSizeVal) {
        textSize.value = liveSize;
        textSizeVal.textContent = liveSize;
      }
    });
    // Bake on drag end: do this BEFORE the existing saveState handler
    // (registered below) so the persisted state captures the post-bake
    // scale=1 form, not the transient mid-drag scale.
    fabricCanvas.on('object:modified', function (e) {
      var obj = e.target;
      if (!isTextLike(obj)) {
        textScaleStart = null;
        return;
      }
      if (obj.scaleX !== 1 || obj.scaleY !== 1) {
        var startSize = (textScaleStart && textScaleStart.fontSize) || obj.fontSize;
        var s = Math.max(obj.scaleX, obj.scaleY);
        var newSize = Math.max(4, Math.round(startSize * s));
        obj.set({
          fontSize: newSize,
          scaleX:   1,
          scaleY:   1,
        });
        obj.setCoords();
        if (fabricCanvas.getActiveObject() === obj && textSize && textSizeVal) {
          textSize.value = newSize;
          textSizeVal.textContent = newSize;
        }
        fabricCanvas.requestRenderAll();
      }
      textScaleStart = null;
    });

    function serialiseCanvas() {
      // Fabric's toJSON writes a whole-canvas snapshot we can rehydrate.
      // We exclude the safe-zone (excludeFromExport: true) so it's not
      // persisted as a user object.
      var json = fabricCanvas.toJSON(['data']);
      // Add canvas dimensions so the print recreator can scale up to
      // the print resolution faithfully.
      json.canvas_w_px = fabricCanvas.getWidth();
      json.canvas_h_px = fabricCanvas.getHeight();
      // And the configured canvas dimensions (the print-resolution target).
      json.print_w_px = CONFIG.canvas_width;
      json.print_h_px = CONFIG.canvas_height;
      return json;
    }

    function saveState() {
      saveStateNoHistory();
      pushHistory();
    }

    // Persist current canvas state to localStorage + form inputs WITHOUT
    // pushing onto the undo history. Called by:
    //   - applyHistory (rehydration — already navigating history)
    //   - saveState's first phase (then pushHistory adds the entry)
    function saveStateNoHistory() {
      var json = serialiseCanvas();
      var payload = JSON.stringify(json);
      try {
        localStorage.setItem(STORAGE_KEY, payload);
      } catch (e) {
        console.warn('[ff-editor] localStorage save failed', e);
      }
      if (canvasDataInput) canvasDataInput.value = payload;
      if (designSummaryInput) {
        var counts = countObjects(json);
        var parts = [];
        if (counts.image) parts.push(counts.image + ' photo' + (counts.image > 1 ? 's' : ''));
        if (counts.text)  parts.push(counts.text  + ' text element' + (counts.text  > 1 ? 's' : ''));
        designSummaryInput.value = parts.length
          ? 'Custom design with ' + parts.join(' and ')
          : 'Custom design (empty)';
      }
    }

    function countObjects(json) {
      var out = { image: 0, text: 0 };
      (json.objects || []).forEach(function (o) {
        if (o.type === 'image') out.image += 1;
        // Count text objects only if they actually have non-whitespace
        // content. Customers can add a text element and clear its text
        // (or never type into it) — those leave a zero-width i-text
        // object in the canvas that doesn't render anything. Counting
        // them produced misleading order summaries like "1 photo and 1
        // text element" when the print only had the photo.
        if (o.type === 'i-text' || o.type === 'text') {
          var t = (o.text || '').replace(/\s+/g, '');
          if (t.length > 0) out.text += 1;
        }
      });
      return out;
    }

    function restoreState() {
      var payload = null;
      try {
        payload = localStorage.getItem(STORAGE_KEY);
      } catch (e) { /* storage disabled — skip */ }
      if (!payload) return;
      var json;
      try {
        json = JSON.parse(payload);
      } catch (e) {
        console.warn('[ff-editor] saved state JSON invalid; discarding', e);
        return;
      }
      fabricCanvas.loadFromJSON(json, function () {
        // After rehydration, re-add the safe zone + guidelines so the
        // overlay markers still appear (excluded from serialisation).
        drawSafeZone();
        drawGuidelines();
        fabricCanvas.requestRenderAll();
      });
    }

    restoreState();
    // Save once at boot so the hidden input is populated even if the
    // customer never edits anything (so the add-to-cart still carries
    // a valid empty-canvas state). Push the seed snapshot to history
    // too — gives undo a known starting point to step back to.
    saveStateNoHistory();
    pushHistory();
    updateUndoRedoButtons();

    // ============================================================
    // Live 3D preview — wraps the customer's flat Fabric canvas onto
    // a cylindrical mug rendered into the product gallery, exactly
    // like the photo-mug personaliser does. Uses the same gallery
    // DOM (sections/sipfull-product.liquid) and the same per-shape
    // calibration (mug_shape metaobject + free-form template overrides).
    //
    // Wrapped in a try/catch so a 3D init failure can never take out
    // the flat editor — customer can always design even if preview
    // breaks for some reason.
    // ============================================================
    try {
      initLive3DPreview();
    } catch (err) {
      console.error('[ff-editor] live 3D preview init failed:', err);
    }

    function initLive3DPreview() {
      if (!window.THREE) {
        console.warn('[ff-editor] Three.js not loaded — preview disabled');
        return;
      }
      var SHAPE = CONFIG.shape;
      if (!SHAPE) {
        console.warn('[ff-editor] no shape data in CONFIG — preview disabled');
        return;
      }
      // Gallery canvases are rendered by sections/sipfull-product.liquid
      // when is_personalised_gallery is true. We render Three.js into
      // these — same selectors the photo-mug personaliser uses.
      var heroCanvases = {
        left:   document.querySelector('[data-fb-canvas][data-fb-view="left"]'),
        centre: document.querySelector('[data-fb-canvas][data-fb-view="centre"]'),
        right:  document.querySelector('[data-fb-canvas][data-fb-view="right"]'),
      };
      if (!heroCanvases.centre) {
        console.warn('[ff-editor] no gallery canvases found — preview disabled');
        return;
      }
      var viewCal = SHAPE.view_calibration || {};

      // Texture canvas — separate 2D canvas we copy the Fabric output
      // onto. Match the personaliser's 4096px ceiling so quality stays
      // crisp at the gallery hero size. CanvasTexture reads from this
      // every time texture.needsUpdate = true.
      var TEX_MAX_W = 4096;
      var srcW = CONFIG.canvas_width  || fabricCanvas.getWidth();
      var srcH = CONFIG.canvas_height || fabricCanvas.getHeight();
      var texAspect = srcW / srcH;
      var texW = Math.min(TEX_MAX_W, Math.max(srcW, 2625));
      var texH = Math.round(texW / texAspect);
      var textureCanvas = document.createElement('canvas');
      textureCanvas.width  = texW;
      textureCanvas.height = texH;
      var textureCtx = textureCanvas.getContext('2d');
      textureCtx.fillStyle = CONFIG.background_colour || '#ffffff';
      textureCtx.fillRect(0, 0, texW, texH);

      function buildGeometry() {
        var radiusTop = 1;
        var radiusBottom = radiusTop * (SHAPE.radius_bottom != null ? SHAPE.radius_bottom : 1);
        var avgRadius = (radiusTop + radiusBottom) / 2;
        var effAspect = SHAPE.design_aspect || (srcW / srcH);
        var wrap = SHAPE.wrap_fraction != null ? SHAPE.wrap_fraction : 1;
        var height = (2 * Math.PI * avgRadius * wrap) / effAspect;
        var thetaLength = 2 * Math.PI * wrap;
        // Centre the wrap on theta=0 (which is +Z, facing the camera).
        // With thetaStart=0 (the personaliser's default), the texture's
        // centre lands at the BACK of the cylinder — customer's design
        // wraps around with the centre hidden, only edges visible at
        // front. Centring the wrap puts the design's middle on the
        // front of the mug, matching customer expectation.
        var thetaStart = -thetaLength / 2;
        return new THREE.CylinderGeometry(
          radiusTop, radiusBottom, height,
          96, 1, true, thetaStart, thetaLength
        );
      }

      var scene = new THREE.Scene();
      var camera = new THREE.PerspectiveCamera(SHAPE.camera_fov || 28, 1, 0.1, 100);
      camera.position.set(0, 0, SHAPE.camera_distance || 5.1);
      camera.lookAt(0, 0, 0);

      var texture = new THREE.CanvasTexture(textureCanvas);
      // sRGB colour space matches the personaliser's setup so the
      // wrapped design's colours match the flat editor's appearance.
      if (THREE.SRGBColorSpace) texture.colorSpace = THREE.SRGBColorSpace;
      texture.anisotropy = 8;

      // material.color multiplies the texture sampling — setting it to a
      // grey value uniformly darkens the rendered mug so the wrapped
      // design doesn't look "pasted on" at full brightness against the
      // bare mug photo's natural lighting falloff. 1.0 = no change.
      var material = new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        side: THREE.FrontSide,
      });
      function applyBrightness() {
        var b = SHAPE.mesh_brightness != null ? SHAPE.mesh_brightness : 1.0;
        material.color.setRGB(b, b, b);
      }
      applyBrightness();
      var mesh = new THREE.Mesh(buildGeometry(), material);
      mesh.rotation.x = SHAPE.mesh_tilt || 0;
      mesh.position.set(SHAPE.mesh_x || 0, SHAPE.mesh_y || 0, 0);
      var ms  = SHAPE.mesh_scale != null ? SHAPE.mesh_scale : 1;
      var msY = SHAPE.mesh_scale_y != null ? SHAPE.mesh_scale_y : 1;
      mesh.scale.set(ms, ms * msY, ms);
      scene.add(mesh);

      // One renderer per view canvas. Each renders the same mesh at
      // a different Y rotation so the customer sees three angles of
      // the wrapped mug (left, centre, right). preserveDrawingBuffer
      // is required so we can drawImage() the WebGL output onto the
      // 2D thumbnail canvases below.
      var renderers = {};
      var thumbCanvases = {};
      var thumbCtxs = {};
      // Clone canvases — duplicate thumbnails in the editor's bottom
      // area (next to the relocated qty/ATC). Same blit pattern as the
      // gallery thumbs.
      var cloneCanvases = {};
      var cloneCtxs = {};
      ['left', 'centre', 'right'].forEach(function (view) {
        var canvas = heroCanvases[view];
        if (!canvas) return;
        var r = new THREE.WebGLRenderer({
          canvas: canvas,
          alpha: true,
          antialias: true,
          preserveDrawingBuffer: true,
        });
        r.setPixelRatio(window.devicePixelRatio || 1);
        r.setClearColor(0x000000, 0);
        renderers[view] = r;
        // Match the personaliser's thumbnail strategy: 2D canvases
        // sitting in the thumb panes get a drawImage() of the
        // WebGL output every frame. Customer sees the wrapped mug
        // on each thumb so they know which view they'd be picking.
        var thumb = document.querySelector('[data-fb-canvas-thumb][data-fb-view="' + view + '"]');
        if (thumb) {
          thumbCanvases[view] = thumb;
          thumbCtxs[view]     = thumb.getContext('2d');
        }
        // ALSO grab the clone canvas in the editor's bottom area
        // (data-fb-canvas-thumb-clone) — same blit pattern, just to a
        // second canvas so the customer sees a live preview from all
        // three angles next to the buy controls without scrolling up.
        var clone = document.querySelector('[data-fb-canvas-thumb-clone][data-fb-view="' + view + '"]');
        if (clone) {
          cloneCanvases[view] = clone;
          cloneCtxs[view]     = clone.getContext('2d');
        }
      });

      function sizeRenderers() {
        Object.keys(renderers).forEach(function (view) {
          var canvas = heroCanvases[view];
          var pane = canvas && canvas.parentElement;
          if (pane && pane.clientWidth) {
            renderers[view].setSize(pane.clientWidth, pane.clientHeight, false);
          }
          // Keep the thumb canvas's backing store in sync with its
          // CSS-displayed size × DPR so the drawImage mirror is crisp.
          var dpr = window.devicePixelRatio || 1;
          var thumb = thumbCanvases[view];
          if (thumb) {
            var tw = thumb.clientWidth  || (thumb.parentElement && thumb.parentElement.clientWidth);
            var th = thumb.clientHeight || (thumb.parentElement && thumb.parentElement.clientHeight);
            if (tw > 0 && th > 0) {
              thumb.width  = Math.round(tw * dpr);
              thumb.height = Math.round(th * dpr);
            }
          }
          // Same for the clone canvas in the editor's bottom area.
          var clone = cloneCanvases[view];
          if (clone) {
            var cw = clone.clientWidth  || (clone.parentElement && clone.parentElement.clientWidth);
            var ch = clone.clientHeight || (clone.parentElement && clone.parentElement.clientHeight);
            if (cw > 0 && ch > 0) {
              clone.width  = Math.round(cw * dpr);
              clone.height = Math.round(ch * dpr);
            }
          }
        });
      }
      sizeRenderers();

      // Tell the gallery reveal gate (mug-color-overlay.js) that the
      // preview has painted. The gate waits for `fb-personaliser:ready`
      // on personalised heroes — but only the TEMPLATED engine fires it.
      // Free-form draws into the same hero canvases, so without this the
      // gate held the bare-mug image (opacity:0) until its safety timeout
      // while the design canvas showed on top — "overlay instant, mug
      // late". Same event + flag the templated engine uses; idempotent.
      var ffReadySignalled = false;
      function signalFfReady() {
        if (ffReadySignalled) return;
        ffReadySignalled = true;
        window.fbPersonaliserReady = true;
        document.dispatchEvent(new CustomEvent('fb-personaliser:ready'));
      }

      function renderAllViews() {
        Object.keys(renderers).forEach(function (view) {
          var cal = viewCal[view] || {};
          mesh.rotation.y = (cal.rotation_offset || 0);
          mesh.rotation.z = (SHAPE.mesh_roll || 0) + (cal.roll_offset || 0);
          renderers[view].render(scene, camera);
          // Mirror to the thumbnail 2D canvas right after rendering.
          var thumbCtx = thumbCtxs[view];
          var thumb    = thumbCanvases[view];
          var heroCanvas = heroCanvases[view];
          if (thumbCtx && thumb && heroCanvas) {
            thumbCtx.clearRect(0, 0, thumb.width, thumb.height);
            thumbCtx.drawImage(heroCanvas, 0, 0, thumb.width, thumb.height);
          }
          // And mirror to the clone canvas in the editor's bottom area
          // (full-width templates only — undefined elsewhere, the if-guard
          // keeps it free).
          var cloneCtx = cloneCtxs[view];
          var clone    = cloneCanvases[view];
          if (cloneCtx && clone && heroCanvas) {
            // Re-sync size every frame in case CSS layout finished after
            // sizeRenderers initially ran (defensive — some browsers
            // compute clientWidth=0 during initial script execution).
            var dpr = window.devicePixelRatio || 1;
            var cw = clone.clientWidth  || (clone.parentElement && clone.parentElement.clientWidth);
            var ch = clone.clientHeight || (clone.parentElement && clone.parentElement.clientHeight);
            if (cw > 0 && ch > 0) {
              var targetW = Math.round(cw * dpr);
              var targetH = Math.round(ch * dpr);
              if (clone.width !== targetW || clone.height !== targetH) {
                clone.width = targetW;
                clone.height = targetH;
              }
            }
            cloneCtx.clearRect(0, 0, clone.width, clone.height);
            cloneCtx.drawImage(heroCanvas, 0, 0, clone.width, clone.height);
          }
        });
        // First full paint done → release the gallery reveal gate.
        signalFfReady();
      }

      // Thumbnail click → switch active hero view. Mirrors the
      // personaliser's activateView() — toggles 'is-active' on the
      // matching hero pane + thumb. CSS in the section drives the
      // hide/show; we just flip the class.
      function activateView(viewId) {
        document.querySelectorAll('[data-fb-hero-pane]').forEach(function (el) {
          el.classList.toggle('is-active', el.getAttribute('data-fb-hero-pane') === viewId);
        });
        document.querySelectorAll('[data-fb-thumb-view]').forEach(function (el) {
          el.classList.toggle('is-active', el.getAttribute('data-fb-thumb-view') === viewId);
        });
        // Sync clone thumbs in the editor's bottom area.
        document.querySelectorAll('[data-ff-thumb-clone]').forEach(function (el) {
          el.classList.toggle('is-active', el.getAttribute('data-ff-thumb-clone-view') === viewId);
        });
      }
      document.querySelectorAll('[data-fb-thumb-view]').forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var v = thumb.getAttribute('data-fb-thumb-view');
          if (v) activateView(v);
        });
      });

      // Clone thumbnails in the editor's bottom area — click → scroll
      // up to the gallery + activate that view. Event delegation on
      // the container so the layered img/canvas/overlay don't have to
      // worry about pointer-events.
      var cloneContainer = document.querySelector('[data-ff-thumb-clones]');
      if (cloneContainer) {
        var galleryEl = document.querySelector('.sp-gallery') ||
                        document.querySelector('[data-fb-gallery]');
        cloneContainer.addEventListener('click', function (e) {
          var clone = e.target.closest && e.target.closest('[data-ff-thumb-clone]');
          if (!clone) return;
          var v = clone.getAttribute('data-ff-thumb-clone-view');
          if (!v) return;
          if (galleryEl && galleryEl.scrollIntoView) {
            galleryEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
          activateView(v);
        });
        // Reflect the initial active state from the gallery thumbs.
        var initialActive = document.querySelector('[data-fb-thumb-view].is-active');
        if (initialActive) {
          var initId = initialActive.getAttribute('data-fb-thumb-view');
          document.querySelectorAll('[data-ff-thumb-clone]').forEach(function (c) {
            c.classList.toggle('is-active', c.getAttribute('data-ff-thumb-clone-view') === initId);
          });
        }
      }

      // Capture a clean, HIGH-RESOLUTION texture from the Fabric canvas.
      //
      // The Fabric canvas's internal pixel size matches its on-screen
      // display size (~700px for a typical layout) — far less than the
      // 4096px texture we're feeding to the cylinder. If we copy at 1:1
      // we end up with the texture upscaling 700px to 4096px, which is
      // exactly the blurriness we were seeing.
      //
      // Fabric's toCanvasElement(multiplier) re-renders all objects at
      // multiplier × resolution: text gets re-rasterised crisp at the
      // bigger size, photos render at their full intrinsic resolution.
      // We pick the multiplier so the output is at least texW wide.
      //
      // Also: hide objects flagged excludeFromExport (guidelines,
      // snap-debug-dot) before capturing — Fabric's excludeFromExport
      // is sadly serialisation-only, doesn't affect visual exports.
      function captureCleanCanvas() {
        var hidden = [];
        fabricCanvas.getObjects().forEach(function (o) {
          if (o.excludeFromExport === true && o.visible !== false) {
            o.visible = false;
            hidden.push(o);
          }
        });
        var fabricW = fabricCanvas.getWidth() || 1;
        // Multiplier needed to scale fabric's display-resolution canvas
        // up to texture resolution. Cap at 6 so a tiny-display canvas
        // doesn't accidentally request a 50× render that locks the GPU.
        var multiplier = Math.min(6, Math.max(1, texW / fabricW));
        var src;
        try { src = fabricCanvas.toCanvasElement(multiplier); }
        catch (e) { src = fabricCanvas.lowerCanvasEl; }
        hidden.forEach(function (o) { o.visible = true; });
        return src;
      }

      // Coalesce texture updates to one per animation frame.
      var updateScheduled = false;
      function scheduleTextureUpdate() {
        if (updateScheduled) return;
        updateScheduled = true;
        requestAnimationFrame(function () {
          updateScheduled = false;
          var src = captureCleanCanvas();
          textureCtx.fillStyle = CONFIG.background_colour || '#ffffff';
          textureCtx.fillRect(0, 0, texW, texH);
          // src is already at ~texW resolution thanks to the multiplier,
          // so this drawImage is effectively a 1:1 copy (no further loss).
          textureCtx.drawImage(src, 0, 0, texW, texH);
          texture.needsUpdate = true;
          renderAllViews();
          // Force the FLAT canvas to repaint too. Without this,
          // text:changed events during inline IText editing update
          // the 3D preview (because we've just rendered to texture
          // with the new state) but not the visible flat canvas —
          // Fabric's own scheduled render gets pre-empted by our
          // toCanvasElement work above.
          fabricCanvas.requestRenderAll();
        });
      }

      // Why we don't use Fabric's `after:render`: it fires for EVERY
      // internal render — including hover/selection state changes,
      // which happen many times per second as the cursor moves over
      // objects. Each fire schedules a texture rebuild + GPU upload
      // + 3 WebGL renders. The cumulative cost makes the editor feel
      // sluggish or even frozen.
      //
      // Instead, listen for high-level CONTENT change events. The
      // 3D preview updates whenever the design actually changes —
      // not on hover/selection state.
      [
        'object:added',     // new photo / text added
        'object:removed',   // delete
        'object:modified',  // drag / scale / rotate finished
        'text:changed',     // typing into IText
      ].forEach(function (evt) {
        fabricCanvas.on(evt, scheduleTextureUpdate);
      });
      // Also update during drag/scale/rotate so customer sees live
      // wrapping as they manipulate. RAF coalescing keeps this cheap.
      ['object:moving', 'object:scaling', 'object:rotating'].forEach(function (evt) {
        fabricCanvas.on(evt, scheduleTextureUpdate);
      });

      // Re-render on window resize (renderers track pane size).
      var resizeRaf = null;
      window.addEventListener('resize', function () {
        if (resizeRaf) cancelAnimationFrame(resizeRaf);
        resizeRaf = requestAnimationFrame(function () {
          sizeRenderers();
          renderAllViews();
        });
      });

      // ----- Per-size shape switching -----
      // When the customer picks a non-default size (12oz Latte, Travel
      // Mug), apply that shape's data: swap the bare-mug photos in the
      // gallery, reload camera/mesh/clip values, rebuild geometry.
      // Switching BACK to a default-shape size (10oz, 11oz, 15oz, etc.)
      // reverts to the snapshotted "primary" data captured at boot.
      var SIZE_MAP = (CONFIG.size_mug_shapes && Object.keys(CONFIG.size_mug_shapes).length)
        ? CONFIG.size_mug_shapes : null;

      // Snapshot the default ("primary") shape so we can swap back to
      // it. The bare-mug image URLs come straight from the section's
      // server-rendered <img> elements; the rest comes from CONFIG.shape.
      function snapshotImg(img) {
        if (!img) return null;
        return { src: img.getAttribute('src'), srcset: img.getAttribute('srcset'), sizes: img.getAttribute('sizes') };
      }
      var PRIMARY_SHAPE = {
        handle:           '__primary__',  // sentinel — never matches a real shape handle
        camera_fov:       SHAPE.camera_fov,
        camera_distance:  SHAPE.camera_distance,
        mesh_tilt:        SHAPE.mesh_tilt,
        mesh_roll:        SHAPE.mesh_roll,
        mesh_x:           SHAPE.mesh_x,
        mesh_y:           SHAPE.mesh_y,
        mesh_scale:       SHAPE.mesh_scale,
        mesh_scale_y:     SHAPE.mesh_scale_y,
        design_aspect:    SHAPE.design_aspect,
        wrap_fraction:    SHAPE.wrap_fraction,
        radius_bottom:    SHAPE.radius_bottom,
        mesh_brightness:  SHAPE.mesh_brightness,
        view_calibration: JSON.parse(JSON.stringify(viewCal || {})),
        // Image snapshots — held as { src, srcset, sizes } so we can
        // restore the original responsive loading behaviour exactly.
        _images: {
          left:        snapshotImg(document.querySelector('[data-fb-hero-pane="left"]   img')),
          centre:      snapshotImg(document.querySelector('[data-fb-hero-pane="centre"] img')),
          right:       snapshotImg(document.querySelector('[data-fb-hero-pane="right"]  img')),
          left_thumb:  snapshotImg(document.querySelector('[data-fb-thumb-view="left"]   img')),
          centre_thumb:snapshotImg(document.querySelector('[data-fb-thumb-view="centre"] img')),
          right_thumb: snapshotImg(document.querySelector('[data-fb-thumb-view="right"]  img')),
        },
      };
      var currentShapeHandle = '__primary__';

      // applyShape — synchronous swap of all visual/render state to a
      // new shape. Called when the customer picks a different size.
      // No fade transition; the swap is immediate (we tried fading,
      // it looked worse than the abrupt swap so we reverted).
      function applyShape(shapeData) {
        if (!shapeData) return;

        // 1. Update SHAPE values so all downstream code uses them.
        SHAPE.camera_fov      = shapeData.camera_fov;
        SHAPE.camera_distance = shapeData.camera_distance;
        SHAPE.mesh_tilt       = shapeData.mesh_tilt;
        SHAPE.mesh_roll       = shapeData.mesh_roll;
        SHAPE.mesh_x          = shapeData.mesh_x;
        SHAPE.mesh_y          = shapeData.mesh_y;
        SHAPE.mesh_scale      = shapeData.mesh_scale;
        SHAPE.mesh_scale_y    = shapeData.mesh_scale_y;
        SHAPE.design_aspect   = shapeData.design_aspect;
        SHAPE.wrap_fraction   = shapeData.wrap_fraction;
        SHAPE.radius_bottom   = shapeData.radius_bottom;
        if (shapeData.mesh_brightness != null) {
          SHAPE.mesh_brightness = shapeData.mesh_brightness;
        }
        if (shapeData.view_calibration) {
          // Replace the per-view calibration in-place (don't reassign
          // viewCal — JS closures hold the original reference).
          ['left', 'centre', 'right'].forEach(function (v) {
            viewCal[v] = shapeData.view_calibration[v] || viewCal[v];
          });
        }

        // 2. Camera + material
        camera.fov = SHAPE.camera_fov;
        camera.position.z = SHAPE.camera_distance;
        camera.updateProjectionMatrix();
        applyBrightness();

        // 3. Mesh transform
        mesh.rotation.x = SHAPE.mesh_tilt || 0;
        mesh.position.x = SHAPE.mesh_x || 0;
        mesh.position.y = SHAPE.mesh_y || 0;
        var msApply  = SHAPE.mesh_scale != null ? SHAPE.mesh_scale : 1;
        var msyApply = SHAPE.mesh_scale_y != null ? SHAPE.mesh_scale_y : 1;
        mesh.scale.set(msApply, msApply * msyApply, msApply);

        // 4. Geometry rebuild (radius_bottom / wrap_fraction / aspect changed)
        mesh.geometry.dispose();
        mesh.geometry = buildGeometry();

        // 5. Swap the bare-mug background images on hero panes + thumbs.
        // IMPORTANT: clear `srcset` first — the section emits both src
        // AND srcset (for responsive loading), and the browser picks
        // from srcset preferentially. Setting only src leaves srcset
        // pointing at the old shape's images, which is what gets shown.
        //
        // For the primary shape, we restore the FULL snapshot (src +
        // srcset + sizes) so the original responsive loading is intact;
        // for override shapes, we just set a single src URL.
        function swapImg(img, url) {
          if (!img || !url) return;
          img.removeAttribute('srcset');
          img.removeAttribute('sizes');
          img.src = url;
        }
        function restoreImg(img, snap) {
          if (!img || !snap) return;
          if (snap.srcset) img.setAttribute('srcset', snap.srcset); else img.removeAttribute('srcset');
          if (snap.sizes)  img.setAttribute('sizes',  snap.sizes);  else img.removeAttribute('sizes');
          if (snap.src)    img.src = snap.src;
        }
        var isPrimary = shapeData.handle === '__primary__';
        ['left', 'centre', 'right'].forEach(function (view) {
          var paneImg  = document.querySelector('[data-fb-hero-pane="' + view + '"] img');
          var thumbImg = document.querySelector('[data-fb-thumb-view="' + view + '"] img');
          // Same swap for the clone thumb in the editor's bottom area
          // (full-width-template products only — null elsewhere).
          var cloneEl  = document.querySelector('[data-ff-thumb-clone-view="' + view + '"]');
          var cloneImg = cloneEl && cloneEl.querySelector(':scope > img:not(.mug-color-overlay)');
          if (isPrimary) {
            restoreImg(paneImg,  shapeData._images[view]);
            restoreImg(thumbImg, shapeData._images[view + '_thumb']);
            restoreImg(cloneImg, shapeData._images[view + '_thumb']);
          } else {
            swapImg(paneImg,  shapeData['blank_' + view + '_url']);
            swapImg(thumbImg, shapeData['blank_' + view + '_thumb_url']);
            swapImg(cloneImg, shapeData['blank_' + view + '_thumb_url']);
          }
        });

        // 6. Re-apply clip masks and re-render.
        ['left', 'centre', 'right'].forEach(applyClipForView);
        scheduleTextureUpdate();

        currentShapeHandle = shapeData.handle;

        // 7. If the calibration panel is open, refresh its slider DOM
        //    values from the new shape's data.
        if (typeof refreshCalibrationPanel === 'function') {
          refreshCalibrationPanel();
        }
      }
      // Hoisted holder — assigned below if the calibration panel
      // initialises. applyShape() above calls it after every switch.
      var refreshCalibrationPanel = null;

      // Detect the current size from the product form. Mirrors the
      // photo-mug personaliser's pattern: this theme uses a Sipfull
      // custom picker at [data-picker="size"] with [data-value]+.active
      // children rather than native radio inputs. Returns the raw size
      // name (caller decides whether it's an override or default).
      function readSelectedSize() {
        var active = document.querySelector('[data-picker="size"] [data-value].active');
        if (active) {
          var v = active.getAttribute('data-value');
          if (v) return v;
        }
        var sel = document.querySelector('[name^="options["]');
        if (sel && sel.value) return sel.value;
        return null;
      }

      function syncShapeFromCurrentSize() {
        if (!SIZE_MAP) return;
        var sizeName = readSelectedSize();
        // sizeName comes back if the picked size has an override entry.
        // If it doesn't (e.g. 11oz uses the default ceramic), fall back
        // to the primary shape we snapshotted at boot.
        var target = sizeName && SIZE_MAP[sizeName] ? SIZE_MAP[sizeName] : PRIMARY_SHAPE;
        if (target.handle === currentShapeHandle) return;  // already on this shape
        applyShape(target);
      }

      // MutationObserver on the size picker — catches active-class
      // changes regardless of trigger (direct click, material-pivot
      // side-effect, programmatic update). The personaliser uses the
      // same pattern; click handlers alone miss material/colour-pivot
      // cases where size changes as a side-effect.
      if (SIZE_MAP) {
        var sizePicker = document.querySelector('[data-picker="size"]');
        if (sizePicker && typeof MutationObserver !== 'undefined') {
          var lastObservedSize = null;
          var sizeObserver = new MutationObserver(function () {
            var active = sizePicker.querySelector('[data-value].active');
            var sizeName = active ? active.getAttribute('data-value') : null;
            if (sizeName !== lastObservedSize) {
              lastObservedSize = sizeName;
              syncShapeFromCurrentSize();
            }
          });
          sizeObserver.observe(sizePicker, {
            attributes: true,
            subtree: true,
            attributeFilter: ['class'],
          });
        }
        // Also listen for native variant-form changes as a fallback.
        document.addEventListener('change', function (e) {
          if (e.target && e.target.name && e.target.name.indexOf('options[') === 0) {
            setTimeout(syncShapeFromCurrentSize, 50);
          }
        });
        // Initial sync — page may load with an override size already
        // selected (e.g. customer reloaded mid-session).
        setTimeout(syncShapeFromCurrentSize, 100);
      }

      // Kick off the first render now that everything's wired up.
      scheduleTextureUpdate();

      // ----- Clip masking (cuts the rendered cylinder to mug silhouette) -----
      // Each view has a 6-element clip array on view_calibration:
      //   [left, right, top, bottom, leftSlant, rightSlant]
      // Slants displace the BOTTOM corners inward (positive) or outward
      // (negative) relative to the top corners — so the mask traces a
      // trapezoid that follows the mug's natural taper instead of a
      // strict rectangle. Same data + behaviour as the photo-mug
      // personaliser's applyClipForView.
      function getViewClip(view) {
        var cal = viewCal[view] || {};
        var c = cal.clip || [];
        return {
          left:       c[0] != null ? c[0] : 0,
          right:      c[1] != null ? c[1] : 100,
          top:        c[2] != null ? c[2] : 0,
          bottom:     c[3] != null ? c[3] : 100,
          leftSlant:  c[4] != null ? c[4] : 0,
          rightSlant: c[5] != null ? c[5] : 0,
        };
      }
      function applyClipForView(view) {
        var canvas = heroCanvases[view];
        var thumb  = thumbCanvases[view];
        var clone  = cloneCanvases[view];
        if (!canvas) return;
        var c = getViewClip(view);
        var blX = c.left  + (c.leftSlant  || 0);
        var brX = c.right - (c.rightSlant || 0);
        var poly = 'polygon(' +
          c.left  + '% ' + c.top    + '%, ' +
          c.right + '% ' + c.top    + '%, ' +
          brX     + '% ' + c.bottom + '%, ' +
          blX     + '% ' + c.bottom + '%)';
        canvas.style.clipPath = poly;
        canvas.style.webkitClipPath = poly;
        if (thumb) {
          thumb.style.clipPath = poly;
          thumb.style.webkitClipPath = poly;
        }
        // Same trapezoidal clip on the clone canvas in the editor's
        // bottom area — without this the cylinder render bleeds out
        // beyond the actual mug body, most visibly on tapered shapes
        // like Latte (the rectangular WebGL output ignores the mug's
        // profile, so the clip is what makes the design appear to
        // wrap to the mug's actual silhouette).
        if (clone) {
          clone.style.clipPath = poly;
          clone.style.webkitClipPath = poly;
        }
      }
      // Apply clips for all views once at boot — uses the values
      // baked into the mug_shape metaobject's view_calibration.
      ['left', 'centre', 'right'].forEach(applyClipForView);

      // ----- Calibration panel (only when ?fb_calibrate=1) -----
      // Mirrors the personaliser's calibration UX so the dev workflow
      // is identical across both products. SHARED section drives the
      // cylinder/mesh/camera; per-view sections drive rotation, roll,
      // and clip silhouette per individual view.
      var calibrateActive = /[?&]fb_calibrate=1\b/.test(window.location.search);
      var calPanel = root.querySelector('[data-ff-calibrate]');
      if (calibrateActive && calPanel) {
        calPanel.classList.add('is-visible');

        // SHARED params: what they drive + how to react to a change.
        var CAL_SHARED = {
          camera_fov:      { decimals: 1, rebuild: false, kind: 'camera' },
          camera_distance: { decimals: 1, rebuild: false, kind: 'camera' },
          design_aspect:   { decimals: 3, rebuild: true,  kind: 'shape'  },
          wrap_fraction:   { decimals: 2, rebuild: true,  kind: 'shape'  },
          mesh_x:          { decimals: 2, rebuild: false, kind: 'mesh'   },
          mesh_y:          { decimals: 2, rebuild: false, kind: 'mesh'   },
          mesh_scale:      { decimals: 2, rebuild: false, kind: 'mesh'   },
          mesh_scale_y:    { decimals: 2, rebuild: false, kind: 'mesh'   },
          radius_bottom:   { decimals: 2, rebuild: true,  kind: 'shape'  },
          mesh_tilt:       { decimals: 2, rebuild: false, kind: 'mesh'   },
          mesh_roll:       { decimals: 2, rebuild: false, kind: 'mesh'   },
          mesh_brightness: { decimals: 2, rebuild: false, kind: 'material' },
        };
        // Per-view params live on viewCal[view] — clip array indices
        // are mapped through clipIdx so we can store/read them as
        // discrete clip_left, clip_right, etc. on the slider data-key.
        var CAL_VIEW = {
          rotation_offset: { decimals: 2, kind: 'rotation', clipIdx: null },
          roll_offset:     { decimals: 3, kind: 'rotation', clipIdx: null },
          clip_left:       { decimals: 1, kind: 'clip',     clipIdx: 0 },
          clip_right:      { decimals: 1, kind: 'clip',     clipIdx: 1 },
          clip_top:        { decimals: 1, kind: 'clip',     clipIdx: 2 },
          clip_bottom:     { decimals: 1, kind: 'clip',     clipIdx: 3 },
          clip_left_slant: { decimals: 1, kind: 'clip',     clipIdx: 4 },
          clip_right_slant:{ decimals: 1, kind: 'clip',     clipIdx: 5 },
        };
        var VIEWS_LIST = ['left', 'centre', 'right'];

        // Helper to read/write per-view values via key like "left.clip_left".
        function readPerView(view, field) {
          var meta = CAL_VIEW[field];
          if (!meta) return 0;
          var cal = viewCal[view] = viewCal[view] || {};
          if (meta.kind === 'rotation') return cal[field] || 0;
          if (meta.kind === 'clip') {
            cal.clip = cal.clip || [];
            return cal.clip[meta.clipIdx] != null ? cal.clip[meta.clipIdx] : 0;
          }
          return 0;
        }
        function writePerView(view, field, value) {
          var meta = CAL_VIEW[field];
          if (!meta) return;
          var cal = viewCal[view] = viewCal[view] || {};
          if (meta.kind === 'rotation') cal[field] = value;
          else if (meta.kind === 'clip') {
            cal.clip = cal.clip || [];
            cal.clip[meta.clipIdx] = value;
          }
        }

        // Snapshot initial values so Reset can restore them.
        var INITIAL_SHARED = {};
        Object.keys(CAL_SHARED).forEach(function (k) {
          INITIAL_SHARED[k] = SHAPE[k] != null ? SHAPE[k] : 0;
        });
        var INITIAL_VIEW = {};
        VIEWS_LIST.forEach(function (view) {
          INITIAL_VIEW[view] = {};
          Object.keys(CAL_VIEW).forEach(function (field) {
            INITIAL_VIEW[view][field] = readPerView(view, field);
          });
        });

        function fmt(v, d) { return parseFloat(v).toFixed(d); }
        var outEl = root.querySelector('[data-ff-cal-out]');

        function refreshOutput() {
          if (!outEl) return;
          var lines = [
            '// Free-form snippet defaults (paste into',
            '// snippets/flipping-bangers-free-form.liquid):',
          ];
          Object.keys(CAL_SHARED).forEach(function (k) {
            lines.push('ff_' + k + ': ' + fmt(SHAPE[k], CAL_SHARED[k].decimals));
          });
          VIEWS_LIST.forEach(function (view) {
            lines.push('// ' + view + ' view (mug_shape.view_calibration):');
            lines.push('  rotation_offset: ' + fmt(readPerView(view, 'rotation_offset'), 2));
            lines.push('  roll_offset:     ' + fmt(readPerView(view, 'roll_offset'), 3));
            lines.push('  clip:            [' +
              fmt(readPerView(view, 'clip_left'), 1) + ', ' +
              fmt(readPerView(view, 'clip_right'), 1) + ', ' +
              fmt(readPerView(view, 'clip_top'), 1) + ', ' +
              fmt(readPerView(view, 'clip_bottom'), 1) + ', ' +
              fmt(readPerView(view, 'clip_left_slant'), 1) + ', ' +
              fmt(readPerView(view, 'clip_right_slant'), 1) + ']');
          });
          outEl.textContent = lines.join('\n');
        }

        function applySharedChange(key) {
          var meta = CAL_SHARED[key];
          if (!meta) return;
          if (meta.kind === 'camera') {
            camera.fov = SHAPE.camera_fov;
            camera.position.z = SHAPE.camera_distance;
            camera.updateProjectionMatrix();
          } else if (meta.kind === 'material') {
            applyBrightness();
          } else {
            // Mesh transform (also covers post-rebuild restore)
            mesh.rotation.x = SHAPE.mesh_tilt || 0;
            mesh.position.x = SHAPE.mesh_x || 0;
            mesh.position.y = SHAPE.mesh_y || 0;
            var ms2  = SHAPE.mesh_scale != null ? SHAPE.mesh_scale : 1;
            var ms2y = SHAPE.mesh_scale_y != null ? SHAPE.mesh_scale_y : 1;
            mesh.scale.set(ms2, ms2 * ms2y, ms2);
          }
          if (meta.rebuild) {
            mesh.geometry.dispose();
            mesh.geometry = buildGeometry();
          }
          renderAllViews();
          refreshOutput();
        }
        function applyViewChange(view, field) {
          var meta = CAL_VIEW[field];
          if (!meta) return;
          if (meta.kind === 'clip') {
            applyClipForView(view);
          }
          // rotation/roll get picked up by renderAllViews's per-view
          // mesh.rotation.y/z assignments — just re-render.
          renderAllViews();
          refreshOutput();
        }

        // Update DOM slider values + display labels from current
        // SHAPE / viewCal. Called once at init AND every time
        // applyShape() switches shape — without the latter, the
        // panel keeps showing the previous shape's values, which
        // causes confusion and overwrites the new shape's data on
        // first slider drag.
        function syncSliderValuesToCurrentShape() {
          Object.keys(CAL_SHARED).forEach(function (key) {
            var input  = calPanel.querySelector('[data-ff-cal="' + key + '"]');
            var output = calPanel.querySelector('[data-ff-cal-val="' + key + '"]');
            if (!input) return;
            input.value = SHAPE[key];
            if (output) output.textContent = fmt(SHAPE[key], CAL_SHARED[key].decimals);
          });
          VIEWS_LIST.forEach(function (view) {
            Object.keys(CAL_VIEW).forEach(function (field) {
              var sel = view + '.' + field;
              var input  = calPanel.querySelector('[data-ff-cal="' + sel + '"]');
              var output = calPanel.querySelector('[data-ff-cal-val="' + sel + '"]');
              if (!input) return;
              var v = readPerView(view, field);
              input.value = v;
              if (output) output.textContent = fmt(v, CAL_VIEW[field].decimals);
            });
          });
          refreshOutput();
        }
        // Expose for applyShape() (declared above the panel block).
        refreshCalibrationPanel = syncSliderValuesToCurrentShape;

        // Wire SHARED sliders' input handlers (one-time bind).
        Object.keys(CAL_SHARED).forEach(function (key) {
          var input  = calPanel.querySelector('[data-ff-cal="' + key + '"]');
          var output = calPanel.querySelector('[data-ff-cal-val="' + key + '"]');
          if (!input) return;
          input.addEventListener('input', function () {
            SHAPE[key] = parseFloat(input.value);
            if (output) output.textContent = fmt(SHAPE[key], CAL_SHARED[key].decimals);
            applySharedChange(key);
          });
        });
        // Wire per-view sliders' input handlers (one-time bind).
        VIEWS_LIST.forEach(function (view) {
          Object.keys(CAL_VIEW).forEach(function (field) {
            var sel = view + '.' + field;
            var input  = calPanel.querySelector('[data-ff-cal="' + sel + '"]');
            var output = calPanel.querySelector('[data-ff-cal-val="' + sel + '"]');
            if (!input) return;
            input.addEventListener('input', function () {
              var v = parseFloat(input.value);
              writePerView(view, field, v);
              if (output) output.textContent = fmt(v, CAL_VIEW[field].decimals);
              applyViewChange(view, field);
            });
          });
        });
        // Initial population.
        syncSliderValuesToCurrentShape();

        // Copy values to clipboard.
        var copyBtn = calPanel.querySelector('[data-ff-cal-copy]');
        if (copyBtn) {
          copyBtn.addEventListener('click', function () {
            if (!outEl) return;
            navigator.clipboard.writeText(outEl.textContent).then(function () {
              var orig = copyBtn.textContent;
              copyBtn.textContent = '✓ Copied';
              setTimeout(function () { copyBtn.textContent = orig; }, 1500);
            });
          });
        }
        // Reset to initial values.
        var resetBtn = calPanel.querySelector('[data-ff-cal-reset]');
        if (resetBtn) {
          resetBtn.addEventListener('click', function () {
            // Restore SHARED
            Object.keys(INITIAL_SHARED).forEach(function (k) {
              SHAPE[k] = INITIAL_SHARED[k];
              var input  = calPanel.querySelector('[data-ff-cal="' + k + '"]');
              var output = calPanel.querySelector('[data-ff-cal-val="' + k + '"]');
              if (input)  input.value = SHAPE[k];
              if (output) output.textContent = fmt(SHAPE[k], CAL_SHARED[k].decimals);
            });
            // Restore per-view
            VIEWS_LIST.forEach(function (view) {
              Object.keys(INITIAL_VIEW[view]).forEach(function (field) {
                var v = INITIAL_VIEW[view][field];
                writePerView(view, field, v);
                var sel = view + '.' + field;
                var input  = calPanel.querySelector('[data-ff-cal="' + sel + '"]');
                var output = calPanel.querySelector('[data-ff-cal-val="' + sel + '"]');
                if (input)  input.value = v;
                if (output) output.textContent = fmt(v, CAL_VIEW[field].decimals);
              });
              applyClipForView(view);
            });
            mesh.geometry.dispose();
            mesh.geometry = buildGeometry();
            applySharedChange('mesh_scale');
            applySharedChange('camera_fov');
            renderAllViews();
            refreshOutput();
          });
        }
        refreshOutput();
        console.log('[ff-editor] calibration panel active');
      }
    }

    // ============================================================
    // Helpers
    // ============================================================

    function parseListField(raw) {
      if (Array.isArray(raw)) return raw;
      if (typeof raw !== 'string') return [];
      // Could be "[\"a\",\"b\"]" or "a,b" depending on Liquid path.
      var trimmed = raw.trim();
      if (!trimmed) return [];
      if (trimmed.charAt(0) === '[') {
        try { return JSON.parse(trimmed); } catch (e) { return []; }
      }
      return trimmed.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    }

    function slugToLabel(slug) {
      return slug.split('-').map(function (s) {
        return s.charAt(0).toUpperCase() + s.slice(1);
      }).join(' ');
    }

    // Map font slug to CSS font-family using the slug→family map.
    // Falls back to the slug itself if not in the map (graceful
    // degradation; user sees a generic font but no error).
    function slugToFontFamily(slug) {
      var family = FONT_FAMILY_MAP[slug] || slug;
      // Quote the family name so multi-word names work in font-family.
      return "'" + family + "', sans-serif";
    }

    function rgbToHex(c) {
      if (!c) return '#000000';
      if (c.charAt(0) === '#') return c;
      // rgb(r,g,b) or rgba(r,g,b,a) — extract numerics.
      var m = c.match(/\d+/g);
      if (!m || m.length < 3) return '#000000';
      function h(n) { return ('0' + parseInt(n, 10).toString(16)).slice(-2); }
      return '#' + h(m[0]) + h(m[1]) + h(m[2]);
    }
  }
})();
